import {
  CheckOutlined,
  CloseOutlined,
  DownOutlined,
  LockOutlined,
  TeamOutlined,
} from '@ant-design/icons';
import { IconButton, ScopeButton } from '@monorepo/shared-lib';
import type { MenuProps } from 'antd';
import { Alert, Dropdown, Space, Spin, Typography } from 'antd';
import { ReactComponent as RefreshIcon } from 'assets/images/icons/refreshIcon.svg';
import { StyledCard } from 'components/Custom/card';
import { useFeatureDot } from 'components/Custom/FeatureDotBadge';
import { ProviderIcon } from 'components/Custom/ProviderIcon';
import {
  CardDescSpan,
  CardTitleLargeSpan,
  CardTitleSpan,
  ColoredTag,
} from 'components/Custom/spans';
import { ScopeTable } from 'components/Custom/table';
import { StyledTooltip } from 'components/Custom/Tooltip';
import { EntityDetailsWrapper } from 'components/Custom/wrappers';
import {
  GROUP_OVERVIEW_ENTITY_COL_WIDTH_PX,
  GroupAddReturnButtonWrap,
  groupOverviewEntityColumnOnCell,
  GroupOverviewEntityIconPlaceholder,
  GroupOverviewEntityNameLink,
  GroupOverviewEntityNameText,
  GroupOverviewEntitySpace,
  GroupOverviewProviderLogoNav,
  GroupOverviewTableActionButton,
  GroupSummaryCard,
  GroupSummaryCardBody,
  GroupSummaryHeader,
  GroupSummaryHeaderLeft,
  GroupSummaryNote,
  GroupSummarySection,
  GroupSummaryTableWrap,
  NextPeriodLineBreak,
} from 'components/OrganisationDetails/group-overview-styled';
import { openInterpretationRulesDrawer } from 'components/OrganisationDetails/interpretation-rules/open-interpretation-rules';
import { HmrcConnectButton } from 'components/OrganisationDetails/HmrcConnectButton';
// import { HmrcTestHeaderButton } from 'components/OrganisationDetails/HmrcTestHeaderButton';
import { entitySupportsDocumentScanning } from 'utils/captureTransactionsAccess';
import {
  UploadTransactionsPanel,
  UploadTransactionsPanelVariant,
} from 'components/OrganisationDetails/UploadTransactionsPanel';
import moment from 'moment';
import * as React from 'react';
import { useTranslation } from 'react-i18next';
import { useCountryLabel } from 'hooks/useCountryLabel';
import { formatDisplayInteger } from 'i18n/arabicDigits';
import { formatDisplayDate } from 'i18n/calendarLabels';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';
import routes from 'routes';
import { FEATURE_DOT_COLOR } from 'shared/config/featureDotsConfig';
import {
  PAGE_SIZE_EXCEL_TRANSACTIONS,
  TRANSACTIONS_FILE_FILTER_TOUR_PENDING_IMPORT_ID_KEY,
} from 'shared/constants/transactionsTourKeys';
import { AccountType, Provider } from 'store/api/enums';
import { getReturnFrequencyLabel } from 'i18n/returnFrequencyLabel';
import { turnDrawerOff, turnMessageOn, turnModalOn } from 'store/app/actions';
import {
  AdvancedUserRightsCodes,
  DataLogStatus,
  EntityStatus,
  MenuSize,
  WindowType,
} from 'store/app/enums';
import { User } from 'store/auth/models';
import { fetchChartOfAccountsForOrganisationRequest } from 'store/chart-of-accounts/actions';
import type { Country } from 'store/common/models';
import {
  fetchAddUrlRequest,
  fetchAllExcelTransactionsRequest,
  fetchExcelFilesMetadataRequest,
  fetchOrganisationContactsRequest,
  fetchTransactionItemsRequest,
  // hmrcFraudHeaderTestRequest,
} from 'store/organisation/actions';
import { Entity, Mapping, Organisation } from 'store/organisation/models';
import { resetReconciliation } from 'store/reconciliation/actions';
import { resetPwaSheetDragState } from 'utils/usePwaBottomSheetDragClose';
import { getEntityOrganisationPath } from 'utils/get-entity-organisation-path';
import {
  shouldFetchOrganisationProviderData,
  shouldRestrictDisconnectedProviderTabs,
} from 'utils/get-provider-type';
import { MappingDetails } from './mapping';

import GenTaxImage from 'assets/images/gen-tax-img-cropped.svg';
import SseNotifications from 'components/ServerSentEvents';
import { ConfirmBoxContainer } from 'containers/ConfirmBox';
import { MessageStates } from 'containers/MessageBox';
import { MessageData } from 'store/app/types';
import {
  getAllExcelTransactionsPage,
  getLastReceiptUploadStatus,
  getLastUploadedReceiptsBatchId,
  // getOrgLoading,
  getOrgMappings,
  getOrgOrganisations,
} from 'store/organisation/selectors';
import {
  getReconciliationGenerating,
  getViewReconciliationData,
} from 'store/reconciliation/selectors';
import { MappingData } from 'store/returns/models';
import {
  clearReconciliationRun,
  loadReconFailure,
  updateReportFieldsRequest,
} from 'store/vat/actions';
import { VatReturnLog } from 'store/vat/models';
import {
  getReconciliationRun,
  getVatReconciliation,
} from 'store/vat/selectors';
import { AdddReturnButton } from 'utils/AddReturnButton';
import { AvatarGroupRenderer } from 'utils/avatar-group-renderer';
import { hasAdvancedUserRight } from 'utils/hasAdvancedUserRight';
import {
  Preference,
  PreferenceActions,
  PreferenceSections,
  usePreferences,
} from 'utils/preferences';
import { AvatarSizes, renderAvatar } from 'utils/render-avatar';
import { calculateNextPeriod } from 'utils/calculate-next-period';
import {
  buildTransactionsRefreshFilters,
  parseSelectTransactionFromSearch,
  SELECT_TRANSACTION_URL_PARAM,
} from 'utils/entityTransactionsRequestFilters';
import {
  shouldShowCaptureAndTransactionsTabs,
  shouldShowCaptureDocumentsPromo,
} from 'utils/captureTransactionsAccess';
import { usePwaMobileNavEnabled } from 'utils/usePwaMobileNavEnabled';
import { usePwaTransactionsBottomActions } from 'components/PwaBottomNav/transactions-actions-context';
import { PwaIcon } from 'pwa/components/Icon';
import {
  isPwaTaxReturnCreationAllowed,
  PWA_ENTITY_TAB_KEYS,
  resolvePwaTabWhileConnectionSettles,
  shouldShowPwaDisconnected,
  type PwaEntityTabKey,
} from 'utils/pwa-nav';
import { setPwaLastEntity } from 'utils/pwa-last-entity';
import { isLeaveEntityNavigationLocked } from 'utils/leave-entity-navigation-lock';
import { shouldSkipExcelFilesMetadataBurst } from 'utils/excelFilesMetadataFetchGuard';
import { shouldSkipChartOfAccountsBurst } from 'utils/chartOfAccountsFetchGuard';
import { shouldSkipTransactionItemsBurst } from 'utils/transactionItemsFetchGuard';
import { useFetchTrackingCategories } from 'hooks/useFetchTrackingCategories';
import {
  isIncomeStatementEnabled,
  isTransactionItemsEnabled,
} from 'utils/feature-flag-utils';
import useWindowDimensions from '../../hooks/useWindowDimensions';
import { CaptureDocumentsPromotionPanel } from './CaptureDocumentsPromotionPanel';
import { CaptureTabSkeleton } from './capture-tab-skeleton';
import { isCaptureDocumentsUsed } from './capture-documents-used';
import { ChartOfAccountsTab } from './chart-of-accounts-tab';
import ConnectFolder from './ConnectFolder';
import { ContactsTab } from './contacts-tab';
import { EntityDetailsCard } from './EntityDetailsCard';
import {
  EXCEL_ENTITY_TUTORIAL_HIDE,
  ExcelEntityGuide,
} from './excel-entity-guide';
import { ReactivationPanel } from './ReactivationPanel';
import { EntityNotConnectedPanel } from './EntityNotConnectedPanel';
import { PwaEntityBar } from 'pwa/components/PwaEntityBar';
import { PwaEntityDisconnected } from 'pwa/components/PwaEntityDisconnected';
import {
  buildPwaReturnListItems,
  PwaReturnsPage,
  type PwaReturnsQueue,
} from 'pwa/components/PwaReturnsPage';
import 'pwa/styles/tokens.css';
import 'pwa/styles/pwa-home.css';
import {
  isCaptureScanPrerequisitesProvider,
  useReceiptsScanPrerequisitesMet,
} from './receipts-scan-prerequisites';
import { ReconciliationTab } from './reconciliation-tab';
import { IncomeStatementTab } from './income-statement-tab';
import { ShowUploadsPanelContext } from './show-uploads-panel-context';
import {
  TAX_RATES_PROMOTION_SIDE_PANEL_WIDTH,
  TaxRatesPromotionBanner,
} from './tax-rates-promotion-banner';
import { TaxRatesTab } from './tax-rates-tab';
import {
  isRescanDrawerReopenComplete,
  markPendingRescanBatchComplete,
  pendingRescanDrawerReopenRef,
  resolveReceiptBatchStatus,
  syncPendingRescanActiveBatchId,
} from './transactions-tab/pending-rescan-drawer-reopen';
import { TransactionsTab } from './transactions-tab';
import { computePendingReviewTransactionCount } from './transactions-tab/entity-transaction-helpers';
import {
  useCaptureTabInitialShell,
  isCaptureTabShellReady,
} from './use-capture-tab-ready';
import { VatCheckerPanel } from './VatCheckerPanel';
import { TrackingCategoriesTab } from './tracking-categories-tab';
import { TransactionItemsTab } from './TransactionItemsTab';

const ENTITY_MAIN_TAB_KEYS = [
  'vat',
  'capture',
  'transactions',
  'reconciliation',
  'income-statement',
] as const;

/** Leading tabs before Reconciliation + Reference (visual order). */
const ENTITY_LEADING_TAB_KEYS = ['vat', 'capture', 'transactions'] as const;

const ENTITY_REFERENCE_TAB_KEYS = [
  'tax-rates',
  'chart-of-accounts',
  'transaction-items',
  'tracking-categories',
  'contacts',
] as const;

const ENTITY_TAB_URL_VALID = new Set<string>([
  ...ENTITY_MAIN_TAB_KEYS,
  ...ENTITY_REFERENCE_TAB_KEYS,
]);

/** Tabs disabled when a third-party provider is not connected properly. */
const ENTITY_TABS_RESTRICTED_WHEN_DISCONNECTED = [
  'capture',
  'transactions',
  'reconciliation',
  ...ENTITY_REFERENCE_TAB_KEYS,
] as const;

const { Text } = Typography;

const ENTITY_DETAILS_BREAKPOINT = 1500;
const ENTITY_DETAILS_HEIGHT_BREAKPOINT = 750;

function isUkVatReturnMapping(
  countryName: string | undefined,
  mappingName: string | undefined
): boolean {
  const country = (countryName ?? '').trim().toLowerCase();
  const isUkCountry =
    country === 'united kingdom' ||
    country === 'uk' ||
    country === 'united kingdom - uk vat return';
  const name = (mappingName ?? '').trim().toLowerCase();
  return isUkCountry && name.includes('uk vat return');
}

const findUserByCreatedByLabel = (
  createdBy: string,
  users: User[] | undefined
): User | undefined => {
  if (!createdBy?.trim() || !users?.length) return undefined;
  const n = createdBy.trim().toLowerCase();
  return users.find((u) => {
    const full = `${u.firstName || ''} ${u.lastName || ''}`
      .trim()
      .toLowerCase();
    if (full && full === n) return true;
    if ((u.email || '').toLowerCase() === n) return true;
    if (u.fullName && u.fullName.toLowerCase() === n) return true;
    return false;
  });
};

const renderGroupMappingCreatedByColumn = (
  _: unknown,
  row: { createdBy: string; entityUsers: User[] }
) => {
  const createdBy = row.createdBy?.trim();
  if (!createdBy) {
    return <Text type="secondary">—</Text>;
  }

  const u = findUserByCreatedByLabel(createdBy, row.entityUsers);
  if (u) {
    const first =
      u.firstName?.trim() || (u.email ? u.email.split('@')[0] : '') || '?';
    const last = u.lastName?.trim() || '';
    const pic =
      u.hasProfilePicture && u.profilePicture ? u.profilePicture : undefined;
    return (
      renderAvatar(first, last, AvatarSizes.DEFAULT, u.userUuid, pic) ?? (
        <Text type="secondary">—</Text>
      )
    );
  }

  if (createdBy.includes('@') && !createdBy.includes(' ')) {
    const local = createdBy.split('@')[0] || 'U';
    return (
      renderAvatar(local, '', AvatarSizes.DEFAULT) ?? (
        <Text type="secondary">—</Text>
      )
    );
  }

  const parts = createdBy.split(/\s+/);
  const first = parts[0] || '';
  const last = parts.length > 1 ? parts.slice(1).join(' ') : '';
  if (first) {
    return (
      renderAvatar(first, last, AvatarSizes.DEFAULT) ?? (
        <Text type="secondary">—</Text>
      )
    );
  }

  return <Text type="secondary">—</Text>;
};

interface OrganisationDetailsProps {
  loading: boolean;
  organisation?: Organisation;
  organisationId?: string; // From URL - allows fetch when organisation prop is still loading
  //disconnectOrganisation: (organisationId: string) => void;
  countries: Country[];
  selectedMaps: Mapping[];
  vatReturnLogs: VatReturnLog[];
  openEditFriendlyName: () => void;
  addReturn: (
    connectionId?: string,
    prefillData?: Pick<
      MappingData,
      | 'countryId'
      | 'typeId'
      | 'returnFrequency'
      | 'periodFromDate'
      | 'returnDueDate'
    >,
    context?: { fromGroup?: boolean; groupId?: string }
  ) => void;
  editReturnMapping: (
    connectionId: string,
    mappingId: string,
    context?: { fromGroup?: boolean; groupId?: string }
  ) => void;
  vatGenerate: (
    connectionId: string,
    mappingId: string,
    dataLogId?: string,
    context?: { fromGroup?: boolean; groupId?: string }
  ) => void;
  connectOrganisation: () => void;
  currentUser?: User;
  loadCustomersAndSuppliers: (connectionId: string) => void;
  activateVatChecker: (
    connectionId: string,
    orgName: string,
    uuid: string
  ) => void;
  handlePreferences: (action: PreferenceActions, preference: Preference) => any;
  menuSize: MenuSize;
  hasAdvancedUserRights: boolean;
  downloadAttachment: (organisationId: string, attachmentId: string) => void;
  checkTaxRatesAccess: () => boolean;
  openReconciliationDrawer?: () => void;
  openUploadTransactions: () => void;
}
export const OrganisationDetails: React.FC<OrganisationDetailsProps> = ({
  loading,
  organisation,
  organisationId: organisationIdProp,
  //disconnectOrganisation,
  countries,
  selectedMaps,
  vatReturnLogs,
  openEditFriendlyName,
  addReturn,
  editReturnMapping,
  vatGenerate,
  connectOrganisation,
  currentUser,
  loadCustomersAndSuppliers,
  activateVatChecker,
  handlePreferences,
  menuSize,
  hasAdvancedUserRights,
  downloadAttachment,
  checkTaxRatesAccess,
  openReconciliationDrawer,
  openUploadTransactions,
}) => {
  const buildGroupAddReturnPrefill = React.useCallback(
    (
      entity: Organisation,
      mapping?: Mapping
    ):
      | Pick<
          MappingData,
          | 'countryId'
          | 'typeId'
          | 'returnFrequency'
          | 'periodFromDate'
          | 'returnDueDate'
        >
      | undefined => {
      if (!mapping?.returnTypeId) return undefined;
      const countryId = entity?.returnTypesEdit?.find(
        (rt) => rt.id === mapping.returnTypeId
      )?.countryId;
      if (!countryId) return undefined;
      return {
        countryId,
        typeId: mapping.returnTypeId,
        returnFrequency: mapping.returnFrequency,
        periodFromDate: mapping.periodFromDate
          ? moment(mapping.periodFromDate).format('YYYY-MM-DD')
          : undefined,
        returnDueDate: mapping.returnDueDate
          ? moment(mapping.returnDueDate).format('YYYY-MM-DD')
          : undefined,
      };
    },
    []
  );

  const { t, i18n } = useTranslation();
  const { localizeCountry, localizeCountryLabel } = useCountryLabel();
  const location = useLocation();
  const navigate = useNavigate();
  const { width: windowWidth, height: windowHeight } = useWindowDimensions();
  const dispatch = useDispatch();
  // const orgLoading = useSelector(getOrgLoading);
  const reconciliation = useSelector(getVatReconciliation);
  const reconciliationRun = useSelector(getReconciliationRun);
  const reconciliationGenerating = useSelector(getReconciliationGenerating);
  const viewReconciliationData = useSelector(getViewReconciliationData);
  const allExcelTransactionsPage = useSelector(getAllExcelTransactionsPage);
  const organisations = useSelector(getOrgOrganisations);
  const mappings = useSelector(getOrgMappings);

  // Resolve organisation from the store when possible so tabs (tax rates, etc.)
  // refresh after create/edit/delete. Fall back to the prop while loading.
  const organisationId = organisation?.uuid ?? organisationIdProp ?? undefined;
  const organisationFromStore = organisationId
    ? organisations?.find((o) => o.uuid === organisationId)
    : undefined;
  const effectiveOrganisation = organisationFromStore ?? organisation;
  const isExcelProvider =
    effectiveOrganisation?.providerName?.toLowerCase() ===
    Provider.EXCEL.toLowerCase();
  const isReceiptsProvider =
    effectiveOrganisation?.providerName?.toLowerCase() ===
    Provider.RECEIPTS.toLowerCase();
  const isGroupProvider =
    effectiveOrganisation?.providerName?.toLowerCase() ===
    Provider.GROUP.toLowerCase();
  const isExcelOrReceiptsProvider = isExcelProvider || isReceiptsProvider;
  const scanPrerequisitesMet = useReceiptsScanPrerequisitesMet(
    organisationId,
    effectiveOrganisation
  );

  const driveScannerEnabledForCapture = true;

  const captureNeedsSidePanelData =
    Boolean(organisationId) &&
    !isExcelProvider &&
    !isGroupProvider &&
    driveScannerEnabledForCapture;

  const captureNeedsTaxSetupData =
    Boolean(organisationId) &&
    isCaptureScanPrerequisitesProvider(effectiveOrganisation?.providerName);

  /** Third-party bookkeeping only — same rule as Transactions tab tracking columns. */
  const shouldFetchTrackingCategories = React.useMemo(() => {
    const providerName = effectiveOrganisation?.providerName?.toLowerCase();
    if (!providerName) return false;
    return (
      providerName !== Provider.EXCEL.toLowerCase() &&
      providerName !== Provider.RECEIPTS.toLowerCase()
    );
  }, [effectiveOrganisation?.providerName]);

  const showCaptureAndTransactionsTabs = shouldShowCaptureAndTransactionsTabs(
    effectiveOrganisation?.providerName
  );
  const pwaMobileNav = usePwaMobileNavEnabled();
  const pwaTransactionsActions = usePwaTransactionsBottomActions();
  const pwaEntityTabsOnly = pwaMobileNav && showCaptureAndTransactionsTabs;

  const pwaReturnCountryByTypeId = React.useMemo(() => {
    const map = new Map<string, string>();
    for (const returnType of organisation?.returnTypesEdit ?? []) {
      if (returnType?.id) {
        map.set(String(returnType.id), returnType.countryName || '—');
      }
    }
    return map;
  }, [organisation?.returnTypesEdit]);

  const pwaReturnListItems = React.useMemo(() => {
    if (!pwaEntityTabsOnly || !selectedMaps?.length) {
      return [];
    }
    return buildPwaReturnListItems(
      selectedMaps,
      vatReturnLogs ?? [],
      pwaReturnCountryByTypeId,
      t,
      i18n.language
    );
  }, [
    pwaEntityTabsOnly,
    selectedMaps,
    vatReturnLogs,
    pwaReturnCountryByTypeId,
    t,
    i18n.language,
  ]);

  /** Disconnected Xero/QBO/Zoho (etc.): only Tax Returns tab — Excel/Receipts exempt.
   * On PWA, taxRates still undefined means that entity has not been checked yet. */
  const providerDisconnected = shouldRestrictDisconnectedProviderTabs(
    effectiveOrganisation
  );
  const restrictNonVatTabs = pwaMobileNav
    ? shouldShowPwaDisconnected(effectiveOrganisation)
    : providerDisconnected;
  const pwaTabHeldForConnectionCheck = React.useRef<{
    organisationId: string;
    tab: string;
  } | null>(null);
  const pwaVisibleTabKeys = React.useMemo((): readonly PwaEntityTabKey[] => {
    if (!pwaEntityTabsOnly) {
      return PWA_ENTITY_TAB_KEYS;
    }
    if (restrictNonVatTabs) {
      return ['vat'];
    }
    if (isExcelProvider) {
      return ['transactions', 'vat'];
    }
    return PWA_ENTITY_TAB_KEYS;
  }, [pwaEntityTabsOnly, restrictNonVatTabs, isExcelProvider]);
  const disconnectedEntityTabTooltip = t(
    'organisation.entity-not-connected-description'
  );

  const parentGroupOrganisation =
    !organisationId || isGroupProvider || !Array.isArray(organisations)
      ? undefined
      : organisations.find((org) => {
          if (
            org?.providerName?.toLowerCase() !== Provider.GROUP.toLowerCase()
          ) {
            return false;
          }
          if (!Array.isArray(org?.groupedConnections)) return false;
          return org.groupedConnections.some(
            (gc) => gc?.uuid === organisationId
          );
        });

  const tabNavRef = React.useRef<HTMLElement | null>(null);

  const handleTabNavWheel = React.useCallback(
    (e: React.WheelEvent<HTMLElement>) => {
      const el = tabNavRef.current;
      if (!el) return;

      // If the tab bar can scroll horizontally, map vertical wheel to horizontal
      // and prevent the page from scrolling up/down while the user is scrolling tabs.
      const maxScrollLeft = el.scrollWidth - el.clientWidth;
      if (maxScrollLeft <= 0) return;

      // Trackpads often emit both deltaX and deltaY; keep native horizontal gestures.
      if (Math.abs(e.deltaX) > Math.abs(e.deltaY)) return;

      const prev = el.scrollLeft;
      const next = Math.min(maxScrollLeft, Math.max(0, prev + e.deltaY));
      if (next !== prev) {
        el.scrollLeft = next;
        e.preventDefault();
      }
    },
    []
  );

  /** Pending-review count for the Transactions nav tab — same source as Pending Review (N). */
  const storedOrganisationForCounts = React.useMemo(
    () =>
      organisationId
        ? organisations?.find((o) => o.uuid === organisationId)
        : undefined,
    [organisations, organisationId]
  );

  const transactionsToReviewCount = React.useMemo(
    () =>
      computePendingReviewTransactionCount(
        storedOrganisationForCounts?.excelFilesMetadata ??
          effectiveOrganisation?.excelFilesMetadata,
        effectiveOrganisation?.providerName
      ),
    [
      storedOrganisationForCounts?.excelFilesMetadata,
      effectiveOrganisation?.excelFilesMetadata,
      effectiveOrganisation?.providerName,
    ]
  );

  const hasDataForOrg =
    allExcelTransactionsPage?.organisationId === organisationId;
  const hasNoExcelFiles =
    isExcelProvider &&
    Array.isArray(effectiveOrganisation?.excelFilesMetadata) &&
    (effectiveOrganisation?.excelFilesMetadata?.length ?? 0) === 0;
  /** Only Excel / Receipts use this upload pipeline; other providers must not get Excel/AI empty-state modals or add-return gating from it. */
  const hasNoTransactions =
    isExcelOrReceiptsProvider &&
    (isExcelProvider ? hasNoExcelFiles : true) &&
    hasDataForOrg &&
    (allExcelTransactionsPage?.total ?? 0) === 0;

  const isOrganisationConnectedForStatus = React.useMemo(() => {
    if (!effectiveOrganisation) return false;
    if (
      effectiveOrganisation.providerName?.toLowerCase() ===
      Provider.GROUP.toLowerCase()
    ) {
      return effectiveOrganisation.status !== EntityStatus.VIEW_ONLY;
    }

    return !!effectiveOrganisation.connectedProperly;
  }, [effectiveOrganisation]);

  const isOrganisationReadyForAddReturn = React.useMemo(() => {
    if (!effectiveOrganisation) return false;
    if (
      effectiveOrganisation.providerName?.toLowerCase() !==
      Provider.GROUP.toLowerCase()
    ) {
      return !!effectiveOrganisation.connectedProperly;
    }

    const groupedIds =
      effectiveOrganisation.groupedConnections
        ?.map((groupedConnection) => groupedConnection.uuid)
        .filter(Boolean) || [];
    if ((groupedIds?.length ?? 0) === 0) return false;

    const memberEntities = (organisations || []).filter(
      (entity) =>
        entity.providerName?.toLowerCase() !== Provider.GROUP.toLowerCase() &&
        groupedIds.includes(entity.uuid)
    );
    if ((memberEntities?.length ?? 0) === 0) return false;

    return groupedIds.every((groupedId) =>
      memberEntities.some(
        (entity) =>
          entity.uuid === groupedId &&
          entity.connectedProperly &&
          entity.hasTaxRates
      )
    );
  }, [effectiveOrganisation, organisations]);

  const organisationForAddReturn = React.useMemo(() => {
    if (!organisation) return organisation;
    return {
      ...organisation,
      connectedProperly: isOrganisationReadyForAddReturn,
      hasTaxRates: isGroupProvider
        ? isOrganisationReadyForAddReturn
        : organisation.hasTaxRates,
    };
  }, [organisation, isGroupProvider, isOrganisationReadyForAddReturn]);

  const organisationForInlineAddReturnButton = React.useMemo(() => {
    if (!organisationForAddReturn) return organisationForAddReturn;
    if (isGroupProvider) return organisationForAddReturn;

    // Keep the bottom Add Return button aligned with the empty-state CTA:
    // for non-group entities it should be gated by connection/readiness,
    // not by whether tax rates are currently loaded on the record.
    return {
      ...organisationForAddReturn,
      hasTaxRates: true,
    };
  }, [organisationForAddReturn, isGroupProvider]);

  // State for active tab key
  const [activeTabKey, setActiveTabKey] = React.useState<string>(() =>
    pwaMobileNav ? 'capture' : 'vat'
  );
  const [pwaReturnsQueue, setPwaReturnsQueue] =
    React.useState<PwaReturnsQueue>('submitted');

  // Remember last entity + path for PWA reopen (localStorage).
  React.useEffect(() => {
    if (!pwaMobileNav || !organisationId) {
      return;
    }
    setPwaLastEntity(organisationId, activeTabKey, location.pathname);
  }, [pwaMobileNav, organisationId, activeTabKey, location.pathname]);

  const showCaptureSkeleton = useCaptureTabInitialShell({
    enabled: activeTabKey === 'capture',
    loading,
    organisationId,
    needsSidePanelData: captureNeedsSidePanelData,
    needsTaxSetupData: captureNeedsTaxSetupData,
  });

  /** Re-evaluate capture promo visibility when the Capture tab shell finishes loading. */
  const [captureShellReadyTick, setCaptureShellReadyTick] = React.useState(0);
  React.useEffect(() => {
    if (
      activeTabKey === 'capture' &&
      !showCaptureSkeleton &&
      organisationId &&
      isCaptureTabShellReady(organisationId)
    ) {
      setCaptureShellReadyTick((tick) => tick + 1);
    }
  }, [activeTabKey, showCaptureSkeleton, organisationId]);

  const [showEntityDetails, setShowEntityDetails] = React.useState(false);
  const [showCaptureFolderPanel, setShowCaptureFolderPanel] =
    React.useState(false);

  React.useEffect(() => {
    if (activeTabKey !== 'capture') {
      setShowCaptureFolderPanel(false);
    }
  }, [activeTabKey]);

  const [showUploadsPanel, setShowUploadsPanel] = React.useState(true);

  React.useEffect(() => {
    if (pwaMobileNav) {
      setShowUploadsPanel(true);
    }
  }, [pwaMobileNav]);

  const [optimisticReceiptRescanFileName, setOptimisticReceiptRescanFileName] =
    React.useState<string | null>(null);

  // Excel entity guide (open by default, user can close; persisted in preferences)
  const excelGuidePanelHide = handlePreferences(PreferenceActions.GET, {
    name: EXCEL_ENTITY_TUTORIAL_HIDE,
  });
  const [showExcelGuide, setShowExcelGuide] = React.useState(
    !excelGuidePanelHide
  );

  const [sseMessage, setSseMessage] = React.useState<any>();

  // Local flag: disable Transactions tab while a receipts upload batch is
  // processing. Toggled by UploadTransactionsPanel via callback when uploads
  // start and when the SSE "BATCH DONE" message arrives.
  const [isReceiptsBatchInProgress, setIsReceiptsBatchInProgress] =
    React.useState(false);
  const [rescanDrawerReopenSignal, setRescanDrawerReopenSignal] =
    React.useState(0);
  const [rescanBatchCompleteSignal, setRescanBatchCompleteSignal] =
    React.useState(0);
  const lastReceiptUploadStatus = useSelector(getLastReceiptUploadStatus);
  const lastUploadedReceiptsBatchId = useSelector(
    getLastUploadedReceiptsBatchId
  );

  const initialContactsRequestedRef = React.useRef<Set<string>>(
    new Set<string>()
  );
  const preferences = usePreferences();

  /* Preference-based last tab (disabled — use ?tab= in URL for refresh instead; restore later if needed).
   * Re-add import: ORGANISATION_DETAILS_ACTIVE_TAB_KEY_PREFIX from shared/constants/transactionsTourKeys
  const saveActiveTabToPreferences = React.useCallback(
    (key: string) => {
      if (currentUser?.userUuid && organisationId) {
        preferences(
          PreferenceActions.SET,
          PreferenceSections.ENTITIES,
          {
            name: `${ORGANISATION_DETAILS_ACTIVE_TAB_KEY_PREFIX}-${organisationId}`,
            value: key,
          },
          currentUser.userUuid
        );
      }
    },
    [currentUser?.userUuid, organisationId, preferences]
  );
  */

  const organisationDetailsPath = React.useMemo(
    () =>
      organisationId
        ? routes.organisations.organisation.replace(':id', organisationId)
        : '',
    [organisationId]
  );

  /** Keep tab in the URL so refresh keeps the same tab.
   * Desktop omits `tab` for VAT (default). On PWA, a missing tab is treated as
   * Capture — so VAT must stay as `?tab=vat` or the first click bounces back. */
  const syncTabToUrl = React.useCallback(
    (key: string) => {
      if (!organisationId || !organisationDetailsPath) return;
      if (isLeaveEntityNavigationLocked()) return;
      if (location.pathname !== organisationDetailsPath) return;
      const params = new URLSearchParams(location.search);
      const keepVatTabInUrl = pwaMobileNav && showCaptureAndTransactionsTabs;
      if (key === 'vat' && !keepVatTabInUrl) {
        params.delete('tab');
      } else {
        params.set('tab', key);
      }
      if (key !== 'transactions') {
        params.delete(SELECT_TRANSACTION_URL_PARAM);
      }
      const nextSearch = params.toString();
      navigate(
        {
          pathname: organisationDetailsPath,
          search: nextSearch ? `?${nextSearch}` : '',
        },
        { replace: true }
      );
    },
    [
      organisationId,
      organisationDetailsPath,
      location.pathname,
      location.search,
      navigate,
      pwaMobileNav,
      showCaptureAndTransactionsTabs,
    ]
  );

  const goToTransactionsTab = React.useCallback(() => {
    setActiveTabKey('transactions');
    syncTabToUrl('transactions');
  }, [syncTabToUrl]);

  const refetchExcelTransactionsFromTabRef = React.useRef<(() => void) | null>(
    null
  );
  const rescanDrawerReopenHandlerRef = React.useRef<(() => void) | null>(null);

  const refetchAllExcelTransactions = React.useCallback(() => {
    if (!organisationId) return;

    dispatch(fetchExcelFilesMetadataRequest(organisationId));

    if (refetchExcelTransactionsFromTabRef.current) {
      refetchExcelTransactionsFromTabRef.current();
      return;
    }

    const pageSizeSaved = handlePreferences(PreferenceActions.GET, {
      name: `${PAGE_SIZE_EXCEL_TRANSACTIONS}-${organisationId}`,
    });
    const pageSize =
      typeof pageSizeSaved === 'number' && Number.isFinite(pageSizeSaved)
        ? pageSizeSaved
        : 10;

    const savedFilters = handlePreferences(PreferenceActions.GET, {
      name: `transactions-tab-filter-state-${organisationId}`,
    }) as Record<string, unknown> | undefined;

    const refreshFilters = buildTransactionsRefreshFilters({
      tableFilters: savedFilters,
    });

    dispatch(
      fetchAllExcelTransactionsRequest(
        organisationId,
        1,
        pageSize,
        refreshFilters
      )
    );
  }, [dispatch, organisationId, handlePreferences]);
  const refetchAllExcelTransactionsRef = React.useRef(
    refetchAllExcelTransactions
  );
  React.useEffect(() => {
    refetchAllExcelTransactionsRef.current = refetchAllExcelTransactions;
  }, [refetchAllExcelTransactions]);

  const markRescanDrawerReopenReady = React.useCallback(() => {
    const pending = pendingRescanDrawerReopenRef.current;
    if (!pending || pending.ready) return;
    pendingRescanDrawerReopenRef.current = { ...pending, ready: true };
    setRescanDrawerReopenSignal((value) => value + 1);
    goToTransactionsTab();
  }, [goToTransactionsTab]);

  const onReceiptsBatchComplete = React.useCallback(
    (importId: string) => {
      if (currentUser?.userUuid) {
        preferences(
          PreferenceActions.SET,
          PreferenceSections.TRANSACTIONS,
          {
            name: TRANSACTIONS_FILE_FILTER_TOUR_PENDING_IMPORT_ID_KEY,
            value: importId,
          },
          currentUser.userUuid
        );
      }
      // Refetch transactions so Overview's "Most recent transaction" shows updated data
      refetchAllExcelTransactions();

      if (pendingRescanDrawerReopenRef.current) {
        markPendingRescanBatchComplete();
        setRescanBatchCompleteSignal((value) => value + 1);
        markRescanDrawerReopenReady();
      }
    },
    [
      currentUser?.userUuid,
      preferences,
      refetchAllExcelTransactions,
      markRescanDrawerReopenReady,
    ]
  );

  // Rescan from review drawer: detect completion while Capture tab is active (SSE may not call batch complete).
  React.useEffect(() => {
    const pending = pendingRescanDrawerReopenRef.current;
    if (!pending || pending.ready) return;

    const metadata = effectiveOrganisation?.excelFilesMetadata;
    if (!metadata) return;

    const syncedPending = syncPendingRescanActiveBatchId(
      pending,
      lastUploadedReceiptsBatchId
    );
    if (syncedPending !== pending) {
      pendingRescanDrawerReopenRef.current = syncedPending;
    }

    const batchStatus = resolveReceiptBatchStatus(lastReceiptUploadStatus);
    const providerName =
      effectiveOrganisation?.providerName?.toLowerCase() ?? '';
    const completion = isRescanDrawerReopenComplete({
      pending: syncedPending,
      metadata,
      batchStatus,
      currentBatchId: lastUploadedReceiptsBatchId,
      isReceiptsOnlyProvider: providerName === Provider.RECEIPTS.toLowerCase(),
      isExcelProvider: providerName === Provider.EXCEL.toLowerCase(),
    });

    if (completion === 'error') {
      pendingRescanDrawerReopenRef.current = null;
      return;
    }

    if (completion === 'processing') {
      if (!syncedPending.sawProcessing) {
        pendingRescanDrawerReopenRef.current = {
          ...syncedPending,
          sawProcessing: true,
        };
      }
      return;
    }

    if (completion === 'complete') {
      markRescanDrawerReopenReady();
    }
  }, [
    effectiveOrganisation?.excelFilesMetadata,
    lastReceiptUploadStatus,
    lastUploadedReceiptsBatchId,
    rescanBatchCompleteSignal,
    markRescanDrawerReopenReady,
  ]);

  // Run the transactions-tab reopen handler once that tab is mounted.
  React.useEffect(() => {
    if (activeTabKey !== 'transactions') return;
    if (!pendingRescanDrawerReopenRef.current?.ready) return;
    rescanDrawerReopenHandlerRef.current?.();
  }, [
    activeTabKey,
    rescanDrawerReopenSignal,
    effectiveOrganisation?.excelFilesMetadata,
  ]);

  // Fetch Excel/receipts files metadata when entity mounts (for Upload panel and Filter by file upload).
  // IMPORTANT: only dispatch once the entity is actually in the store. On a hard
  // refresh effects fire before FETCH_ORGANISATIONS_SUCCESS hydrates the store,
  // and a success that arrived before the org existed would silently no-op in
  // the reducer — leaving metadata permanently missing.
  React.useEffect(() => {
    if (loading || !organisationId) return;
    const org =
      effectiveOrganisation ??
      organisations?.find((o) => o?.uuid === organisationId);
    if (!org) return;
    // `excelFilesMetadata !== undefined` is the post-fetch signal (an empty array
    // is a legitimate "no files" result and must not trigger another fetch).
    if (org.excelFilesMetadata !== undefined) return;
    if (shouldSkipExcelFilesMetadataBurst(organisationId)) return;
    dispatch(fetchExcelFilesMetadataRequest(organisationId));
  }, [
    dispatch,
    loading,
    organisationId,
    effectiveOrganisation?.excelFilesMetadata,
    organisations,
  ]);

  // Fetch chart of accounts once the entity exists in the store (so the success
  // handler can attach accounts to that org). Third-party providers (Xero, Zoho,
  // QuickBooks, etc.) only fetch when connected; Excel/Receipts always fetch.
  React.useEffect(() => {
    if (loading || !organisationId) return;
    const org = organisations?.find((o) => o?.uuid === organisationId);
    if (!org) return;
    if (!shouldFetchOrganisationProviderData(org)) return;
    if (
      org.chartOfAccountsLoaded ||
      (Array.isArray(org.chartOfAccounts) && org.chartOfAccounts.length > 0)
    ) {
      return;
    }
    if (shouldSkipChartOfAccountsBurst(organisationId)) return;
    dispatch(fetchChartOfAccountsForOrganisationRequest(organisationId));
  }, [dispatch, loading, organisationId, organisations]);

  // Fetch contacts once per entity so the Transactions tab can show a dropdown
  // for the Name column backed by contactId/contactUuid.
  React.useEffect(() => {
    if (loading || !organisationId) return;
    const org =
      effectiveOrganisation ??
      organisations?.find((o) => o?.uuid === organisationId);
    const connectionId = org?.connectionId;
    if (!connectionId) return;
    if (initialContactsRequestedRef.current.has(connectionId)) return;
    initialContactsRequestedRef.current.add(connectionId);
    // From the entity side we don't want to show a "no contacts" error/toast
    // when there are simply no contacts yet, so we pass suppressMessages=true.
    dispatch(fetchOrganisationContactsRequest(connectionId));
  }, [dispatch, loading, organisationId, effectiveOrganisation, organisations]);

  // Fetch tracking categories at entity entry (Transactions tab uses them for columns).
  useFetchTrackingCategories(
    organisationId,
    effectiveOrganisation,
    shouldFetchTrackingCategories
  );

  // Fetch transaction items at entity entry (third-party bookkeeping providers only).
  React.useEffect(() => {
    if (!isTransactionItemsEnabled()) return;
    if (loading || !organisationId || !shouldFetchTrackingCategories) return;
    const org =
      effectiveOrganisation ??
      organisations?.find((o) => o?.uuid === organisationId);
    if (!org) return;
    if (!shouldFetchOrganisationProviderData(org)) return;
    if (org.transactionItems !== undefined) return;
    if (shouldSkipTransactionItemsBurst(organisationId)) return;
    dispatch(fetchTransactionItemsRequest(organisationId));
  }, [
    dispatch,
    loading,
    organisationId,
    shouldFetchTrackingCategories,
    effectiveOrganisation?.transactionItems,
    effectiveOrganisation?.connectedProperly,
    effectiveOrganisation?.providerName,
  ]);

  // Determine tax rates access based on advanced user rights (only once `organisation` is loaded — otherwise
  // `hasAdvancedUserRight` returns false with no record and staff users appear to "lack" the right, which
  // incorrectly strips `?tab=tax-rates` on refresh before the entity finishes loading).
  const lacksTaxRatesRight =
    !!organisation &&
    !!hasAdvancedUserRights &&
    !hasAdvancedUserRight(
      currentUser,
      organisation as any,
      AdvancedUserRightsCodes.CREATE_TAX_RATES
    );

  const reconciliationTabDisabled = false;
  const incomeStatementTabDisabled = !isIncomeStatementEnabled();

  // Active tab from URL (?tab=…). Default is VAT when the param is absent or legacy overview.
  // Tab changes from the UI call syncTabToUrl so refresh keeps the same tab.
  React.useEffect(() => {
    if (!organisationId || !organisationDetailsPath) return;
    // Do not rewrite the URL after the user has already navigated away (e.g. PWA Home).
    if (isLeaveEntityNavigationLocked()) return;
    if (location.pathname !== organisationDetailsPath) return;
    const params = new URLSearchParams(location.search);
    const raw = params.get('tab');
    let tabNormalized =
      !raw || !raw.trim()
        ? pwaMobileNav && showCaptureAndTransactionsTabs
          ? restrictNonVatTabs
            ? 'vat'
            : isExcelProvider
            ? 'transactions'
            : 'capture'
          : 'vat'
        : raw.trim() === 'overview'
        ? 'vat'
        : raw.trim();

    if (pwaEntityTabsOnly && isExcelProvider && tabNormalized === 'capture') {
      tabNormalized = 'transactions';
      params.set('tab', tabNormalized);
      navigate(
        {
          pathname: organisationDetailsPath,
          search: params.toString() ? `?${params.toString()}` : '',
        },
        { replace: true }
      );
      setActiveTabKey(tabNormalized);
      return;
    }

    if (
      pwaMobileNav &&
      effectiveOrganisation?.providerName &&
      !showCaptureAndTransactionsTabs
    ) {
      navigate(routes.organisations.all, { replace: true });
      return;
    }

    if (
      pwaEntityTabsOnly &&
      !(pwaVisibleTabKeys as readonly string[]).includes(tabNormalized)
    ) {
      tabNormalized = pwaVisibleTabKeys[0] ?? 'vat';
      params.set('tab', tabNormalized);
      navigate(
        {
          pathname: organisationDetailsPath,
          search: params.toString() ? `?${params.toString()}` : '',
        },
        { replace: true }
      );
      setActiveTabKey(tabNormalized);
      return;
    }

    const selectTransaction = parseSelectTransactionFromSearch(location.search);
    if (
      (isExcelProvider || isReceiptsProvider) &&
      selectTransaction &&
      tabNormalized !== 'transactions'
    ) {
      tabNormalized = 'transactions';
      params.set('tab', 'transactions');
      navigate(
        {
          pathname: organisationDetailsPath,
          search: params.toString() ? `?${params.toString()}` : '',
        },
        { replace: true }
      );
    }

    if (incomeStatementTabDisabled) {
      if (
        tabNormalized === 'income-statement' ||
        (isExcelOrReceiptsProvider && tabNormalized === 'reconciliation')
      ) {
        params.delete('tab');
        navigate(
          {
            pathname: organisationDetailsPath,
            search: params.toString() ? `?${params.toString()}` : '',
          },
          { replace: true }
        );
        setActiveTabKey('vat');
        return;
      }
    } else {
      if (isExcelOrReceiptsProvider && tabNormalized === 'reconciliation') {
        params.set('tab', 'income-statement');
        navigate(
          {
            pathname: organisationDetailsPath,
            search: params.toString() ? `?${params.toString()}` : '',
          },
          { replace: true }
        );
        setActiveTabKey('income-statement');
        return;
      }

      if (!isExcelOrReceiptsProvider && tabNormalized === 'income-statement') {
        params.delete('tab');
        navigate(
          {
            pathname: organisationDetailsPath,
            search: params.toString() ? `?${params.toString()}` : '',
          },
          { replace: true }
        );
        setActiveTabKey('vat');
        return;
      }
    }

    if (!ENTITY_TAB_URL_VALID.has(tabNormalized)) {
      params.delete('tab');
      navigate(
        {
          pathname: organisationDetailsPath,
          search: params.toString() ? `?${params.toString()}` : '',
        },
        { replace: true }
      );
      setActiveTabKey('vat');
      return;
    }

    if (lacksTaxRatesRight && tabNormalized === 'tax-rates') {
      params.delete('tab');
      navigate(
        {
          pathname: organisationDetailsPath,
          search: params.toString() ? `?${params.toString()}` : '',
        },
        { replace: true }
      );
      setActiveTabKey('vat');
      return;
    }

    if (
      !shouldFetchTrackingCategories &&
      (tabNormalized === 'tracking-categories' ||
        tabNormalized === 'transaction-items')
    ) {
      params.delete('tab');
      navigate(
        {
          pathname: organisationDetailsPath,
          search: params.toString() ? `?${params.toString()}` : '',
        },
        { replace: true }
      );
      setActiveTabKey('vat');
      return;
    }

    if (pwaMobileNav && effectiveOrganisation?.providerName && organisationId) {
      const held =
        pwaTabHeldForConnectionCheck.current?.organisationId === organisationId
          ? pwaTabHeldForConnectionCheck.current.tab
          : null;
      const resolved = resolvePwaTabWhileConnectionSettles(
        tabNormalized,
        effectiveOrganisation,
        held
      );
      pwaTabHeldForConnectionCheck.current = resolved.rememberedTab
        ? { organisationId, tab: resolved.rememberedTab }
        : null;
      if (resolved.tab !== tabNormalized) {
        if (resolved.tab === 'vat') {
          params.delete('tab');
        } else {
          params.set('tab', resolved.tab);
        }
        navigate(
          {
            pathname: organisationDetailsPath,
            search: params.toString() ? `?${params.toString()}` : '',
          },
          { replace: true }
        );
        setActiveTabKey(resolved.tab);
        return;
      }
    } else if (
      effectiveOrganisation?.providerName &&
      restrictNonVatTabs &&
      (ENTITY_TABS_RESTRICTED_WHEN_DISCONNECTED as readonly string[]).includes(
        tabNormalized
      )
    ) {
      params.delete('tab');
      navigate(
        {
          pathname: organisationDetailsPath,
          search: params.toString() ? `?${params.toString()}` : '',
        },
        { replace: true }
      );
      setActiveTabKey('vat');
      return;
    }

    // Only once `effectiveOrganisation` is loaded — otherwise
    // `shouldShowCaptureAndTransactionsTabs` is false with no provider and
    // incorrectly strips `?tab=capture|transactions` on refresh before the entity finishes loading.
    if (
      effectiveOrganisation?.providerName &&
      !showCaptureAndTransactionsTabs &&
      (tabNormalized === 'capture' || tabNormalized === 'transactions')
    ) {
      params.delete('tab');
      navigate(
        {
          pathname: organisationDetailsPath,
          search: params.toString() ? `?${params.toString()}` : '',
        },
        { replace: true }
      );
      setActiveTabKey('vat');
      return;
    }

    setActiveTabKey(tabNormalized);

    if (
      !pwaEntityTabsOnly &&
      (tabNormalized === 'vat' || raw?.trim() === 'overview')
    ) {
      params.delete('tab');
      navigate(
        {
          pathname: organisationDetailsPath,
          search: params.toString() ? `?${params.toString()}` : '',
        },
        { replace: true }
      );
    }
  }, [
    organisationId,
    organisationDetailsPath,
    location.search,
    location.pathname,
    navigate,
    lacksTaxRatesRight,
    showCaptureAndTransactionsTabs,
    effectiveOrganisation?.providerName,
    effectiveOrganisation?.connectedProperly,
    effectiveOrganisation?.taxRates,
    isExcelProvider,
    isReceiptsProvider,
    isExcelOrReceiptsProvider,
    shouldFetchTrackingCategories,
    restrictNonVatTabs,
    pwaMobileNav,
    pwaEntityTabsOnly,
  ]);

  /* Load last active tab from user preferences (disabled — restore with saveActiveTabToPreferences above)
  React.useEffect(() => {
    if (!currentUser?.userUuid || !organisationId) return;
    const savedTabKey = preferences(
      PreferenceActions.GET,
      PreferenceSections.ENTITIES,
      {
        name: `${ORGANISATION_DETAILS_ACTIVE_TAB_KEY_PREFIX}-${organisationId}`,
      },
      currentUser.userUuid
    );
    const validKeys = [
      'vat',
      'capture',
      'tax-rates',
      'chart-of-accounts',
      'transactions',
      'reconciliation',
      'tracking-categories',
      'contacts',
    ];
    if (
      typeof savedTabKey === 'string' &&
      savedTabKey.trim() &&
      validKeys.includes(savedTabKey)
    ) {
      if (lacksTaxRatesRight && savedTabKey === 'tax-rates') {
        return;
      }
      if (reconciliationTabDisabled && savedTabKey === 'reconciliation') {
        return;
      }
      setActiveTabKey(savedTabKey);
    }
  }, [
    currentUser?.userUuid,
    organisationId,
    isExcelOrReceiptsProvider,
    preferences,
    lacksTaxRatesRight,
    reconciliationTabDisabled,
  ]);
  */

  React.useEffect(() => {
    if (sseMessage) {
      const obj = JSON.parse(sseMessage);
      if (obj) {
        let uploadSource;
        if (obj.uploadSource === 'ONE_DRIVE') {
          uploadSource = 'One Drive';
        } else if (obj.uploadSource === 'MANUAL') {
          uploadSource = 'generate.TAX';
        } else if (obj.uploadSource === 'GOOGLE_DRIVE') {
          uploadSource = 'Google Drive';
        } else if (obj.uploadSource === 'EMAIL') {
          uploadSource = 'Email';
        }

        if (
          obj.status == 'STARTED' &&
          (obj.uploadSource == 'GOOGLE_DRIVE' ||
            obj.uploadSource == 'ONE_DRIVE' ||
            obj.uploadSource == 'EMAIL')
        ) {
          const message: MessageData = {
            title: '',
            description: t('organisation.uploading-files-in-progress', {
              source: uploadSource,
            }),
            type: MessageStates.INFO,
          };
          dispatch(turnMessageOn(message));
        }
        if (
          obj.status == 'FINISHED' &&
          (obj.uploadSource == 'GOOGLE_DRIVE' ||
            obj.uploadSource == 'ONE_DRIVE' ||
            obj.uploadSource == 'EMAIL')
        ) {
          if (obj.data) {
            const data = obj.data;

            const successCount = data?.success || 0;
            const errorCount = data?.error || 0;

            const successLabel =
              successCount === 1
                ? t('organisation.file-singular')
                : t('organisation.files-plural');
            const errorLabel =
              errorCount === 1
                ? t('organisation.file-singular')
                : t('organisation.files-plural');

            const message: MessageData = {
              title: t('common.success'),
              description: (
                <span>
                  {t('organisation.files-uploaded-via-ready', {
                    source: uploadSource,
                  })}
                  <br />
                  {t('organisation.successfully-uploaded-count', {
                    count: successCount,
                    label: successLabel,
                  })}
                  <br />
                  {errorCount > 0 && (
                    <span>
                      {t('organisation.errors-in-count', {
                        count: errorCount,
                        label: errorLabel,
                      })}
                    </span>
                  )}
                </span>
              ),
              type: MessageStates.SUCCESS,
            };
            dispatch(turnMessageOn(message));
            refetchAllExcelTransactionsRef.current();
          }
        }
      }
    }
  }, [sseMessage, dispatch]);

  const {
    showDot: showReconciliationMarker,
    dismiss: dismissReconciliationMarker,
  } = useFeatureDot('entities-reconciliation-tab');
  const { showDot: showCaptureMarker, dismiss: dismissCaptureMarker } =
    useFeatureDot('entities-capture-tab');
  const {
    showDot: showTransactionsMarker,
    dismiss: dismissTransactionsMarker,
  } = useFeatureDot('entities-transactions-tab');

  const hasUnsavedReconciliation =
    reconciliationGenerating ||
    (!!reconciliationRun &&
      reconciliationRun.connectionId === effectiveOrganisation?.connectionId);

  // Sync active tab to URL (?tab=…) so refresh keeps the same tab
  const handleTabChange = (key: string) => {
    const isLeavingReconciliation =
      activeTabKey === 'reconciliation' && key !== 'reconciliation';

    if (isLeavingReconciliation && hasUnsavedReconciliation) {
      const continueWithoutSaving = () => {
        dispatch(resetReconciliation());
        dispatch(loadReconFailure());
        dispatch(clearReconciliationRun());
        setActiveTabKey(key);
        syncTabToUrl(key);
      };

      const modalData = {
        type: WindowType.WARNING,
        title: t('organisation.your-reconciliation-is-not-saved'),
        icon: <img src={GenTaxImage} alt="" />,
        component: (
          <ConfirmBoxContainer
            callback={continueWithoutSaving}
            okText={t('vat-report.continue-without-saving')}
            description={t(
              'organisation.reconciliation-unsaved-leave-description'
            )}
          />
        ),
      };
      dispatch(turnModalOn(modalData));
      return;
    }

    if (isLeavingReconciliation) {
      // Reconciliation should always reopen from default list/banner view
      // (especially saved-report viewing mode) when user returns to the tab.
      dispatch(resetReconciliation());
      dispatch(loadReconFailure());
      dispatch(clearReconciliationRun());
    }

    if (key === 'reconciliation') {
      if (reconciliationTabDisabled) return;
      dismissReconciliationMarker();
    }
    if (key === 'income-statement') {
      if (incomeStatementTabDisabled) return;
    }
    if (key === 'capture') {
      dismissCaptureMarker();
    }
    if (key === 'transactions') {
      dismissTransactionsMarker();
    }
    // Check access when switching to tax-rates tab
    if (key === 'tax-rates') {
      const hasAccess = checkTaxRatesAccess();
      if (!hasAccess) {
        return; // Prevent tab change
      }
    }

    if (
      restrictNonVatTabs &&
      (ENTITY_TABS_RESTRICTED_WHEN_DISCONNECTED as readonly string[]).includes(
        key
      )
    ) {
      return;
    }

    if (key === 'capture') {
      dispatch(turnDrawerOff());
      resetPwaSheetDragState();
    }

    setActiveTabKey(key);
    syncTabToUrl(key);
  };

  const getCountry = (iso: string) => localizeCountry(iso, iso);

  const handleAddReturn = (
    connectionId?: string,
    prefillData?: Pick<
      MappingData,
      | 'countryId'
      | 'typeId'
      | 'returnFrequency'
      | 'periodFromDate'
      | 'returnDueDate'
    >,
    context?: { fromGroup?: boolean; groupId?: string }
  ) => {
    addReturn(connectionId, prefillData, context);
  };

  const hasTransactionsForOrg =
    allExcelTransactionsPage?.organisationId === organisationId &&
    (allExcelTransactionsPage?.total ?? 0) > 0;

  const captureDocumentsUsed = React.useMemo(
    () =>
      isCaptureDocumentsUsed({
        organisationId,
        organisation: effectiveOrganisation,
        hasTransactions:
          effectiveOrganisation?.providerName?.toLowerCase() !==
          Provider.EXCEL.toLowerCase()
            ? hasTransactionsForOrg
            : undefined,
      }),
    [
      organisationId,
      effectiveOrganisation,
      effectiveOrganisation?.excelFilesMetadata,
      hasTransactionsForOrg,
      captureShellReadyTick,
    ]
  );

  const openCaptureDocumentsTab = React.useCallback(() => {
    dispatch(turnDrawerOff());
    resetPwaSheetDragState();
    setActiveTabKey('capture');
    syncTabToUrl('capture');
  }, [dispatch, syncTabToUrl]);

  const renderVatCheckerPanel = () => (
    <VatCheckerPanel
      organisation={organisation}
      loadCustomersAndSuppliers={loadCustomersAndSuppliers}
      activateVatChecker={activateVatChecker}
      pwaMobileNav={pwaEntityTabsOnly}
    />
  );

  const renderCaptureDocumentsPromotionPanel = () => (
    <CaptureDocumentsPromotionPanel
      organisation={organisation}
      onOpenCaptureTab={openCaptureDocumentsTab}
    />
  );

  const showCaptureDocumentsPromo =
    shouldShowCaptureDocumentsPromo(effectiveOrganisation?.providerName) &&
    organisation?.status !== EntityStatus.VIEW_ONLY &&
    !captureDocumentsUsed &&
    !restrictNonVatTabs;

  const showCsvInfoModal = () => {
    dispatch(
      turnModalOn({
        title: t('organisation.csv-format'),
        type: WindowType.FORM,
        component: (
          <div style={{ padding: '20px', textAlign: 'center' }}>
            <CardDescSpan>
              CSV (Comma-Separated Values) is a simple file format used to store
              tabular data. It's a text file where each line represents a row of
              data, and values are separated by commas. CSV files are widely
              compatible and can be opened in spreadsheet applications like
              Excel, Google Sheets, or any text editor. This format is ideal for
              importing transaction data into generate.TAX as it maintains data
              integrity and is easy to work with.
            </CardDescSpan>
          </div>
        ),
      })
    );
  };

  const showExcelInfoModal = () => {
    dispatch(
      turnModalOn({
        title: t('organisation.excel-format'),
        type: WindowType.FORM,
        component: (
          <div style={{ padding: '20px', textAlign: 'center' }}>
            <CardDescSpan>
              Microsoft Excel files (.xlsx or .xls) are spreadsheet files that
              can contain multiple sheets, formulas, and formatting. Excel files
              are perfect for organizing complex transaction data with multiple
              columns and rows. When uploading Excel files to generate.TAX, the
              system will automatically extract the transaction data and map it
              to the appropriate fields for tax return generation.
            </CardDescSpan>
          </div>
        ),
      })
    );
  };

  const onScanFilesUploaded = React.useCallback(() => {
    // No success toast; live status in the panel is enough
  }, []);

  const getVariant = (): UploadTransactionsPanelVariant => {
    const provider = effectiveOrganisation?.providerName?.toLowerCase();
    if (provider === Provider.EXCEL.toLowerCase()) {
      return 'excel';
    } else if (provider === Provider.RECEIPTS.toLowerCase()) {
      return 'receipts';
    } else {
      return '3rdParty';
    }
  };

  const handleOpenInterpretationRules = React.useCallback(() => {
    if (!organisationId) return;
    openInterpretationRulesDrawer({
      dispatch,
      t,
      organisationId,
    });
  }, [dispatch, organisationId, t]);

  const renderUploadTransactionsPanel = (options?: {
    firstBoxTopRightExtras?: React.ReactNode;
    uploadDocumentsTopRightExtras?: React.ReactNode;
  }) => (
    <UploadTransactionsPanel
      variant={getVariant()}
      organisationId={organisationId}
      organisation={effectiveOrganisation}
      onShowCsvInfo={showCsvInfoModal}
      onShowExcelInfo={showExcelInfoModal}
      onUploadTransactions={openUploadTransactions}
      onGoToTransactionsTab={
        effectiveOrganisation?.providerName?.toLowerCase() !==
        Provider.EXCEL.toLowerCase()
          ? goToTransactionsTab
          : undefined
      }
      onFilesUploaded={
        effectiveOrganisation?.providerName?.toLowerCase() !==
        Provider.EXCEL.toLowerCase()
          ? onScanFilesUploaded
          : undefined
      }
      onReceiptsBatchStatusChange={
        effectiveOrganisation?.providerName?.toLowerCase() !==
        Provider.EXCEL.toLowerCase()
          ? setIsReceiptsBatchInProgress
          : undefined
      }
      onReceiptsBatchComplete={
        effectiveOrganisation?.providerName?.toLowerCase() !==
        Provider.EXCEL.toLowerCase()
          ? onReceiptsBatchComplete
          : undefined
      }
      hasTransactions={
        effectiveOrganisation?.providerName?.toLowerCase() !==
        Provider.EXCEL.toLowerCase()
          ? hasTransactionsForOrg
          : undefined
      }
      allExcelTransactionsPage={allExcelTransactionsPage}
      firstBoxTopRightExtras={options?.firstBoxTopRightExtras}
      uploadDocumentsTopRightExtras={options?.uploadDocumentsTopRightExtras}
      optimisticRescanFileName={optimisticReceiptRescanFileName}
      onOptimisticRescanShown={() => setOptimisticReceiptRescanFileName(null)}
      onOpenInterpretationRules={
        entitySupportsDocumentScanning(effectiveOrganisation?.providerName)
          ? handleOpenInterpretationRules
          : undefined
      }
    />
  );

  const renderReactivationPanel = () => (
    <ReactivationPanel organisation={organisation} />
  );

  const renderGroupMappingsSummary = () => {
    if (!isGroupProvider || !effectiveOrganisation) {
      return null;
    }

    const groupedIds =
      effectiveOrganisation.groupedConnections
        ?.map((groupedConnection) => groupedConnection.uuid)
        .filter(Boolean) || [];
    if ((groupedIds?.length ?? 0) === 0) {
      return null;
    }

    const memberEntities = groupedIds
      .map((groupedId) => organisations?.find((o) => o.uuid === groupedId))
      .filter(Boolean) as Organisation[];
    if ((memberEntities?.length ?? 0) === 0) {
      return null;
    }

    const memberEntityIds = new Set(
      memberEntities.map((entity) => entity.uuid)
    );
    const memberMappings = (mappings || []).filter((mapping) =>
      memberEntityIds.has(mapping.organisationId)
    );
    // Mappings owned directly by the group org. The group's own mappingId
    // is what we post when creating a tax submission from the group view.
    const groupOwnedMappings = (mappings || []).filter(
      (mapping) => mapping.organisationId === effectiveOrganisation.uuid
    );
    const returnTypeIds = Array.from(
      new Set(memberMappings.map((mapping) => mapping.returnTypeId))
    );

    const getNextPeriod = (mapping: Mapping | undefined) => {
      if (!mapping?.periodFromDate || !mapping?.returnFrequency) return '';
      const periodValue = calculateNextPeriod(
        mapping.periodFromDate,
        mapping.returnFrequency
      )[0];
      return typeof periodValue === 'string'
        ? periodValue
        : String(periodValue || '');
    };

    const renderNextPeriod = (mapping: Mapping | undefined) => {
      const value = getNextPeriod(mapping);
      if (!value) return '';
      const [start = '', end = ''] = value.split(' - ');
      if (!end) return value;
      return (
        <span>
          {start} -<NextPeriodLineBreak />
          {end}
        </span>
      );
    };

    const getHeaderTitle = (mapping: Mapping | undefined) => {
      if (!mapping) return '';
      const owner = memberEntities.find(
        (entity) => entity.uuid === mapping.organisationId
      );
      const countryName = owner?.returnTypesEdit?.find(
        (returnType) => returnType.id === mapping.returnTypeId
      )?.countryName;
      return countryName
        ? `${localizeCountry(countryName, countryName)} - ${
            mapping.mappingName
          }`
        : mapping.mappingName;
    };

    const renderGroupEntityColumn = (
      _: unknown,
      row: {
        entityName: string;
        entityUuid: string;
        providerName?: string;
        shortCode?: string;
      }
    ) => {
      const to = getEntityOrganisationPath(row.entityUuid, row.providerName);
      return (
        <GroupOverviewEntitySpace>
          <GroupOverviewProviderLogoNav
            providerName={row.providerName}
            shortCode={row.shortCode}
            organisationId={row.entityUuid}
            onClick={(e) => e.stopPropagation()}
          >
            {row.providerName ? (
              <ProviderIcon
                providerName={row.providerName}
                alt={row.providerName || ''}
                width={25}
                style={{ display: 'block', paddingTop: '4px' }}
              />
            ) : (
              <GroupOverviewEntityIconPlaceholder>
                {(row.entityName || '?').charAt(0).toUpperCase()}
              </GroupOverviewEntityIconPlaceholder>
            )}
          </GroupOverviewProviderLogoNav>
          <GroupOverviewEntityNameLink
            to={to}
            onClick={(e) => {
              clearOrganisationDetailsTabStorage();
              e.stopPropagation();
            }}
          >
            <GroupOverviewEntityNameText ellipsis={{ tooltip: true }}>
              {row.entityName}
            </GroupOverviewEntityNameText>
          </GroupOverviewEntityNameLink>
        </GroupOverviewEntitySpace>
      );
    };

    if ((returnTypeIds?.length ?? 0) === 0) {
      return (
        <GroupSummarySection key="group-summary-no-return-types">
          <GroupSummaryCard>
            <GroupSummaryCardBody>
              <GroupSummaryHeader>
                <GroupSummaryHeaderLeft>
                  <CardTitleLargeSpan>
                    {t('organisation.no-returns-found')}
                  </CardTitleLargeSpan>
                  <GroupSummaryNote>
                    {t('organisation.add-return-to-start-note')}
                  </GroupSummaryNote>
                </GroupSummaryHeaderLeft>
              </GroupSummaryHeader>
              <GroupSummaryTableWrap>
                <ScopeTable
                  columns={[
                    {
                      title: t('common.entity'),
                      dataIndex: 'entityName',
                      key: 'entityName',
                      width: GROUP_OVERVIEW_ENTITY_COL_WIDTH_PX,
                      onCell: groupOverviewEntityColumnOnCell,
                      render: renderGroupEntityColumn,
                    },
                    {
                      title: t('submissions.actions-header'),
                      dataIndex: 'actions',
                      key: 'actions',
                      width: 140,
                      render: (_, row) => row.actions,
                    },
                  ]}
                  mainLocaleKey="users"
                  originalDataSource={memberEntities.map((entity) => {
                    const hasEditRight =
                      !hasAdvancedUserRights ||
                      hasAdvancedUserRight(
                        currentUser,
                        entity as any,
                        AdvancedUserRightsCodes.CREATE_EDIT_RETURN
                      );
                    const canAddThisEntity =
                      !!entity.connectedProperly &&
                      !!entity.hasTaxRates &&
                      hasEditRight;
                    const addReturnRecord = {
                      ...entity,
                      connectedProperly: !!entity.connectedProperly,
                      hasTaxRates: !!entity.hasTaxRates,
                    };
                    const groupAddReturnPrefill =
                      buildGroupAddReturnPrefill(entity);

                    return {
                      key: `no-return-type-${entity.uuid}`,
                      entityName: entity.friendlyName,
                      entityUuid: entity.uuid,
                      providerName: entity.providerName,
                      shortCode: entity.shortCode,
                      actions: (
                        <StyledTooltip
                          title={
                            canAddThisEntity
                              ? ''
                              : !hasEditRight
                              ? t('global.edit-return-rights-warning')
                              : t('group.add-return-disabled-not-ready')
                          }
                          placement="top"
                          arrow={false}
                        >
                          <GroupAddReturnButtonWrap>
                            <AdddReturnButton
                              record={addReturnRecord}
                              onClick={() =>
                                handleAddReturn(
                                  entity.connectionId,
                                  groupAddReturnPrefill,
                                  {
                                    fromGroup: true,
                                    groupId: effectiveOrganisation?.uuid,
                                  }
                                )
                              }
                              size="small"
                            />
                          </GroupAddReturnButtonWrap>
                        </StyledTooltip>
                      ),
                    };
                  })}
                  tableDataSource={memberEntities.map((entity) => {
                    const hasEditRight =
                      !hasAdvancedUserRights ||
                      hasAdvancedUserRight(
                        currentUser,
                        entity as any,
                        AdvancedUserRightsCodes.CREATE_EDIT_RETURN
                      );
                    const canAddThisEntity =
                      !!entity.connectedProperly &&
                      !!entity.hasTaxRates &&
                      hasEditRight;
                    const addReturnRecord = {
                      ...entity,
                      connectedProperly: !!entity.connectedProperly,
                      hasTaxRates: !!entity.hasTaxRates,
                    };
                    const groupAddReturnPrefill =
                      buildGroupAddReturnPrefill(entity);

                    return {
                      key: `no-return-type-${entity.uuid}`,
                      entityName: entity.friendlyName,
                      entityUuid: entity.uuid,
                      providerName: entity.providerName,
                      shortCode: entity.shortCode,
                      actions: (
                        <StyledTooltip
                          title={
                            canAddThisEntity
                              ? ''
                              : !hasEditRight
                              ? t('global.edit-return-rights-warning')
                              : t('group.add-return-disabled-not-ready')
                          }
                          placement="top"
                          arrow={false}
                        >
                          <GroupAddReturnButtonWrap>
                            <AdddReturnButton
                              record={addReturnRecord}
                              onClick={() =>
                                handleAddReturn(
                                  entity.connectionId,
                                  groupAddReturnPrefill,
                                  {
                                    fromGroup: true,
                                    groupId: effectiveOrganisation?.uuid,
                                  }
                                )
                              }
                              size="small"
                            />
                          </GroupAddReturnButtonWrap>
                        </StyledTooltip>
                      ),
                    };
                  })}
                  pagination={false}
                  rowsPerPage={(memberEntities?.length ?? 0) || 1}
                  enableColumnsCustomization={false}
                  rowHeight={45}
                  showSizeChanger={false}
                  getNewPageSize={() => {}}
                  transactionType=""
                  reportId=""
                  haveBorder
                />
              </GroupSummaryTableWrap>
            </GroupSummaryCardBody>
          </GroupSummaryCard>
        </GroupSummarySection>
      );
    }

    return returnTypeIds.map((returnTypeId) => {
      const mappingsForReturnType = memberMappings.filter(
        (mapping) => mapping.returnTypeId === returnTypeId
      );
      const representative = mappingsForReturnType[0];
      const representativeOwner = representative
        ? memberEntities.find(
            (entity) => entity.uuid === representative.organisationId
          )
        : undefined;
      const representativeCountryName =
        representativeOwner?.returnTypesEdit?.find(
          (returnType) => returnType.id === returnTypeId
        )?.countryName;
      const showGroupHmrcSection = isUkVatReturnMapping(
        representativeCountryName,
        representative?.mappingName
      );
      const showHmrcConnected = Boolean(
        (effectiveOrganisation?.hmrcConnectionUuid ?? '').trim()
      );
      // The group's own mapping for this returnType — this is what we POST
      // as the mappingId when creating a tax submission from the group view.
      const groupMappingForReturnType = groupOwnedMappings.find(
        (mapping) => mapping.returnTypeId === returnTypeId
      );
      const mappingsToCheckForInProgress = [
        ...mappingsForReturnType,
        ...(groupMappingForReturnType ? [groupMappingForReturnType] : []),
      ];
      const hasGroupInProgressSubmission = mappingsToCheckForInProgress.some(
        (mapping) =>
          (vatReturnLogs || []).some(
            (log) =>
              log.mappingId === mapping.mappingId &&
              DataLogStatus[log.status] !== DataLogStatus.CANCELLED &&
              DataLogStatus[log.status] !== DataLogStatus.SUBMITTED
          )
      );
      const hasMappingsForAllMembers =
        (mappingsForReturnType?.length ?? 0) === (memberEntities?.length ?? 0);
      const nextPeriods = mappingsForReturnType.map((mapping) =>
        getNextPeriod(mapping)
      );
      const hasSameNextPeriod =
        (nextPeriods?.length ?? 0) > 0 &&
        new Set(nextPeriods.map((period) => (period || '').trim())).size === 1;
      const cashAccountingValues = mappingsForReturnType.map((mapping) =>
        Boolean(mapping.cashAccounting)
      );
      const hasSameCashAccounting =
        (cashAccountingValues?.length ?? 0) > 0 &&
        new Set(cashAccountingValues).size === 1;
      const areGroupMappingsInSync =
        hasMappingsForAllMembers && hasSameNextPeriod && hasSameCashAccounting;
      const canCreateSubmissionForRepresentative =
        !!representative &&
        !!groupMappingForReturnType &&
        isOrganisationReadyForAddReturn &&
        areGroupMappingsInSync &&
        !(
          currentUser &&
          AccountType[currentUser.accountType] === AccountType.STAFF &&
          !hasAdvancedUserRight(
            currentUser,
            organisation as any,
            AdvancedUserRightsCodes.CREATE_SUBMISSION
          )
        );

      return (
        <GroupSummarySection key={`group-summary-${returnTypeId}`}>
          <GroupSummaryCard>
            <GroupSummaryCardBody>
              <GroupSummaryHeader>
                <GroupSummaryHeaderLeft>
                  <CardTitleLargeSpan>
                    {getHeaderTitle(representative)}
                  </CardTitleLargeSpan>
                  <GroupSummaryNote>
                    {t('group.consolidated-return-note')}
                  </GroupSummaryNote>
                </GroupSummaryHeaderLeft>
                {currentUser &&
                  AccountType[currentUser.accountType] !==
                    AccountType.CLIENT && (
                    <Space wrap>
                      {showGroupHmrcSection && (
                        <>
                          <HmrcConnectButton
                            connected={showHmrcConnected}
                            onConnect={() => {
                              const organisationId = (
                                effectiveOrganisation?.uuid ?? ''
                              ).trim();
                              if (!organisationId) return;
                              dispatch(
                                fetchAddUrlRequest(
                                  'hmrc',
                                  organisationId,
                                  '/main',
                                  'HMRC'
                                )
                              );
                            }}
                          />
                          {/* <HmrcTestHeaderButton
                            disabled={!showHmrcConnected}
                            loading={orgLoading}
                            onTest={() => {
                              const orgConnectionId = (
                                effectiveOrganisation?.connectionId ?? ''
                              ).trim();
                              if (!orgConnectionId) return;
                              dispatch(
                                hmrcFraudHeaderTestRequest(orgConnectionId)
                              );
                            }}
                          /> */}
                        </>
                      )}
                      {isPwaTaxReturnCreationAllowed() ? (
                        <StyledTooltip
                          title={
                            currentUser &&
                            AccountType[currentUser.accountType] ===
                              AccountType.STAFF &&
                            !hasAdvancedUserRight(
                              currentUser,
                              organisation as any,
                              AdvancedUserRightsCodes.CREATE_SUBMISSION
                            )
                              ? t('global.create-submission-rights-warning')
                              : !areGroupMappingsInSync
                              ? t(
                                  'group.create-submission-disabled-out-of-sync'
                                )
                              : ''
                          }
                          placement="top"
                          arrow={false}
                        >
                          <GroupOverviewTableActionButton
                            type="primary" //@ts-ignore
                            width="185px"
                            height="32px"
                            onClick={() =>
                              groupMappingForReturnType &&
                              organisation?.connectionId &&
                              vatGenerate(
                                organisation.connectionId,
                                groupMappingForReturnType.mappingId,
                                undefined,
                                {
                                  fromGroup: true,
                                  groupId: effectiveOrganisation?.uuid,
                                }
                              )
                            }
                            disabled={!canCreateSubmissionForRepresentative}
                          >
                            {t('organisation.create-tax-submission')}
                          </GroupOverviewTableActionButton>
                        </StyledTooltip>
                      ) : null}
                    </Space>
                  )}
              </GroupSummaryHeader>
              <GroupSummaryTableWrap>
                <ScopeTable
                  columns={[
                    {
                      title: t('common.entity'),
                      dataIndex: 'entityName',
                      key: 'entityName',
                      width: GROUP_OVERVIEW_ENTITY_COL_WIDTH_PX,
                      onCell: groupOverviewEntityColumnOnCell,
                      render: renderGroupEntityColumn,
                    },
                    {
                      title: t('organisation.created-by'),
                      dataIndex: 'createdBy',
                      key: 'createdBy',
                      width: 90,
                      align: 'center',
                      render: renderGroupMappingCreatedByColumn,
                    },
                    {
                      title: t('organisation.created-on'),
                      dataIndex: 'createdOn',
                      key: 'createdOn',
                      width: 120,
                    },
                    {
                      title: t('planner.frequency'),
                      dataIndex: 'returnFrequencyLabel',
                      key: 'returnFrequencyLabel',
                      width: 140,
                    },
                    {
                      title: t('organisation.next-period'),
                      dataIndex: 'nextPeriod',
                      key: 'nextPeriod',
                      width: 100,
                      render: (_, row) => row.nextPeriod,
                    },
                    {
                      title: t('organisation.next-due-date'),
                      dataIndex: 'nextDueDate',
                      key: 'nextDueDate',
                      width: 130,
                    },
                    {
                      title: t('submissions.actions-header'),
                      dataIndex: 'actions',
                      key: 'actions',
                      width: 140,
                      render: (_, row) => row.actions,
                    },
                  ]}
                  mainLocaleKey="users"
                  originalDataSource={memberEntities.map((entity) => {
                    const mapping = mappingsForReturnType.find(
                      (m) => m.organisationId === entity.uuid
                    );
                    const hasEditRight =
                      !hasAdvancedUserRights ||
                      hasAdvancedUserRight(
                        currentUser,
                        entity as any,
                        AdvancedUserRightsCodes.CREATE_EDIT_RETURN
                      );
                    const canEditThisEntity =
                      !!mapping &&
                      !hasGroupInProgressSubmission &&
                      !!entity.connectedProperly &&
                      !!entity.hasTaxRates &&
                      hasEditRight;
                    const canAddThisEntity =
                      !mapping &&
                      !hasGroupInProgressSubmission &&
                      !!entity.connectedProperly &&
                      !!entity.hasTaxRates &&
                      hasEditRight;
                    const addReturnRecord = {
                      ...entity,
                      connectedProperly:
                        !!entity.connectedProperly &&
                        !hasGroupInProgressSubmission,
                      hasTaxRates:
                        !!entity.hasTaxRates && !hasGroupInProgressSubmission,
                    };
                    const prefillFromExistingMapping =
                      mappingsForReturnType.find(
                        (m) => m.organisationId !== entity.uuid
                      );
                    const groupAddReturnPrefill = buildGroupAddReturnPrefill(
                      entity,
                      prefillFromExistingMapping
                    );

                    return {
                      key: `${returnTypeId}-${entity.uuid}`,
                      entityName: entity.friendlyName,
                      entityUuid: entity.uuid,
                      providerName: entity.providerName,
                      shortCode: entity.shortCode,
                      createdBy: mapping?.createdBy || '',
                      entityUsers: entity.users,
                      createdOn: mapping?.createdDate
                        ? formatDisplayDate(
                            mapping.createdDate,
                            'DD/MM/YYYY',
                            i18n.language
                          )
                        : '',
                      returnFrequencyLabel: mapping?.returnFrequency
                        ? getReturnFrequencyLabel(mapping.returnFrequency, t)
                        : '',
                      nextPeriod: renderNextPeriod(mapping),
                      nextDueDate: mapping?.returnDueDate
                        ? formatDisplayDate(
                            mapping.returnDueDate,
                            'DD/MM/YYYY',
                            i18n.language
                          )
                        : '',
                      actions: mapping ? (
                        <StyledTooltip
                          title={
                            canEditThisEntity
                              ? t('organisation.edit-tax-return-tooltip')
                              : !hasEditRight
                              ? t('global.edit-return-rights-warning')
                              : hasGroupInProgressSubmission
                              ? t(
                                  'organisation.edit-tax-return-disabled-submission-tooltip'
                                )
                              : t(
                                  'organisation.edit-tax-return-disabled-disconnected-tooltip'
                                )
                          }
                          placement="top"
                          arrow={false}
                        >
                          <GroupOverviewTableActionButton
                            type="default" //@ts-ignore
                            width="99px"
                            height="25px"
                            onClick={() =>
                              editReturnMapping(
                                entity.connectionId,
                                mapping.mappingId,
                                {
                                  fromGroup: true,
                                  groupId: effectiveOrganisation?.uuid,
                                }
                              )
                            }
                            disabled={!canEditThisEntity}
                          >
                            {t('group.overview-table-edit-return')}
                          </GroupOverviewTableActionButton>
                        </StyledTooltip>
                      ) : (
                        <StyledTooltip
                          title={
                            canAddThisEntity
                              ? ''
                              : !hasEditRight
                              ? t('global.edit-return-rights-warning')
                              : hasGroupInProgressSubmission
                              ? t(
                                  'organisation.edit-tax-return-disabled-submission-tooltip'
                                )
                              : t('group.add-return-disabled-not-ready')
                          }
                          placement="top"
                          arrow={false}
                        >
                          <GroupAddReturnButtonWrap>
                            <AdddReturnButton
                              record={addReturnRecord}
                              onClick={() =>
                                handleAddReturn(
                                  entity.connectionId,
                                  groupAddReturnPrefill,
                                  {
                                    fromGroup: true,
                                    groupId: effectiveOrganisation?.uuid,
                                  }
                                )
                              }
                              size="small"
                            />
                          </GroupAddReturnButtonWrap>
                        </StyledTooltip>
                      ),
                    };
                  })}
                  tableDataSource={memberEntities.map((entity) => {
                    const mapping = mappingsForReturnType.find(
                      (m) => m.organisationId === entity.uuid
                    );
                    const hasEditRight =
                      !hasAdvancedUserRights ||
                      hasAdvancedUserRight(
                        currentUser,
                        entity as any,
                        AdvancedUserRightsCodes.CREATE_EDIT_RETURN
                      );
                    const canEditThisEntity =
                      !!mapping &&
                      !hasGroupInProgressSubmission &&
                      !!entity.connectedProperly &&
                      !!entity.hasTaxRates &&
                      hasEditRight;
                    const canAddThisEntity =
                      !mapping &&
                      !hasGroupInProgressSubmission &&
                      !!entity.connectedProperly &&
                      !!entity.hasTaxRates &&
                      hasEditRight;
                    const addReturnRecord = {
                      ...entity,
                      connectedProperly:
                        !!entity.connectedProperly &&
                        !hasGroupInProgressSubmission,
                      hasTaxRates:
                        !!entity.hasTaxRates && !hasGroupInProgressSubmission,
                    };
                    const prefillFromExistingMapping =
                      mappingsForReturnType.find(
                        (m) => m.organisationId !== entity.uuid
                      );
                    const groupAddReturnPrefill = buildGroupAddReturnPrefill(
                      entity,
                      prefillFromExistingMapping
                    );

                    return {
                      key: `${returnTypeId}-${entity.uuid}`,
                      entityName: entity.friendlyName,
                      entityUuid: entity.uuid,
                      providerName: entity.providerName,
                      shortCode: entity.shortCode,
                      createdBy: mapping?.createdBy || '',
                      entityUsers: entity.users,
                      createdOn: mapping?.createdDate
                        ? formatDisplayDate(
                            mapping.createdDate,
                            'DD/MM/YYYY',
                            i18n.language
                          )
                        : '',
                      returnFrequencyLabel: mapping?.returnFrequency
                        ? getReturnFrequencyLabel(mapping.returnFrequency, t)
                        : '',
                      nextPeriod: renderNextPeriod(mapping),
                      nextDueDate: mapping?.returnDueDate
                        ? formatDisplayDate(
                            mapping.returnDueDate,
                            'DD/MM/YYYY',
                            i18n.language
                          )
                        : '',
                      actions: mapping ? (
                        <StyledTooltip
                          title={
                            canEditThisEntity
                              ? t('organisation.edit-tax-return-tooltip')
                              : !hasEditRight
                              ? t('global.edit-return-rights-warning')
                              : hasGroupInProgressSubmission
                              ? t(
                                  'organisation.edit-tax-return-disabled-submission-tooltip'
                                )
                              : t(
                                  'organisation.edit-tax-return-disabled-disconnected-tooltip'
                                )
                          }
                          placement="top"
                          arrow={false}
                        >
                          <GroupOverviewTableActionButton
                            type="default" //@ts-ignore
                            width="99px"
                            height="25px"
                            onClick={() =>
                              editReturnMapping(
                                entity.connectionId,
                                mapping.mappingId,
                                {
                                  fromGroup: true,
                                  groupId: effectiveOrganisation?.uuid,
                                }
                              )
                            }
                            disabled={!canEditThisEntity}
                          >
                            {t('group.overview-table-edit-return')}
                          </GroupOverviewTableActionButton>
                        </StyledTooltip>
                      ) : (
                        <StyledTooltip
                          title={
                            canAddThisEntity
                              ? ''
                              : !hasEditRight
                              ? t('global.edit-return-rights-warning')
                              : hasGroupInProgressSubmission
                              ? t(
                                  'organisation.edit-tax-return-disabled-submission-tooltip'
                                )
                              : t('group.add-return-disabled-not-ready')
                          }
                          placement="top"
                          arrow={false}
                        >
                          <GroupAddReturnButtonWrap>
                            <AdddReturnButton
                              record={addReturnRecord}
                              onClick={() =>
                                handleAddReturn(
                                  entity.connectionId,
                                  groupAddReturnPrefill,
                                  {
                                    fromGroup: true,
                                    groupId: effectiveOrganisation?.uuid,
                                  }
                                )
                              }
                              size="small"
                            />
                          </GroupAddReturnButtonWrap>
                        </StyledTooltip>
                      ),
                    };
                  })}
                  pagination={false}
                  rowsPerPage={(memberEntities?.length ?? 0) || 1}
                  enableColumnsCustomization={false}
                  rowHeight={45}
                  showSizeChanger={false}
                  getNewPageSize={() => {}}
                  transactionType=""
                  reportId=""
                  haveBorder
                />
              </GroupSummaryTableWrap>
              {groupMappingForReturnType && (
                <MappingDetails
                  mapping={groupMappingForReturnType}
                  countryName={
                    effectiveOrganisation?.returnTypesEdit?.find(
                      (rt) => rt.id === groupMappingForReturnType.returnTypeId
                    )?.countryName
                  }
                  vatReturnLogs={vatReturnLogs}
                  connectionId={organisation?.connectionId}
                  vatGenerate={vatGenerate}
                  editReturnMapping={editReturnMapping}
                  connected={!!organisation?.connectedProperly}
                  currentUser={currentUser}
                  handlePreferences={handlePreferences}
                  organisation={effectiveOrganisation}
                  hasAdvancedUserRights={hasAdvancedUserRights}
                  updateReportFields={updateReportFields}
                  bodyOnly
                />
              )}
            </GroupSummaryCardBody>
          </GroupSummaryCard>
        </GroupSummarySection>
      );
    });
  };

  /** Excel / Receipts: guides + upload/scanner UI (Capture Documents tab only). Omit ConnectFolder when it is shown in the side column. */
  const renderExcelReceiptsCapturePanels = (options?: {
    /** Wide Capture tab: outer shell already applies 20px top padding */
    inlineWithSidePanel?: boolean;
  }) => {
    return (
      <div
        style={{
          paddingTop: options?.inlineWithSidePanel ? 0 : '20px',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
        }}
      >
        {isExcelProvider && showExcelGuide && !pwaMobileNav && (
          <div
            style={{
              width: '100%',
              maxWidth: 1250,
              marginBottom: 0,
            }}
          >
            <ExcelEntityGuide
              setShowCard={setShowExcelGuide}
              handlePreferences={handlePreferences}
            />
          </div>
        )}
        {renderUploadTransactionsPanel()}
      </div>
    );
  };

  const renderReturnsContent = ({
    includeExcelReceiptsCapture,
    inlineWithSidePanel = false,
  }: {
    includeExcelReceiptsCapture: boolean;
    /** Wide Tax Returns + entity details: stretch main column inside shared max-width wrapper */
    inlineWithSidePanel?: boolean;
  }) => {
    /** Outer shell already applies 20px top; inner blocks must not duplicate or the left column sits lower than the side panel */
    const sectionPadTop = inlineWithSidePanel ? 0 : pwaEntityTabsOnly ? 0 : '20px';
    const returnsContentPad = inlineWithSidePanel
      ? 0
      : pwaEntityTabsOnly
      ? '0'
      : '0 40px';
    return (
      <div
        className={
          pwaEntityTabsOnly ? 'scope-pwa-vat-returns-content' : undefined
        }
        style={{
          padding: returnsContentPad,
          width: '100%',
          margin: pwaEntityTabsOnly ? 0 : 'auto',
          ...(inlineWithSidePanel ? {} : { maxWidth: pwaEntityTabsOnly ? undefined : 1250 }),
          ...(inlineWithSidePanel
            ? {
                display: 'flex',
                flexDirection: 'column',
                gap: 20,
              }
            : {}),
        }}
      >
        {organisation?.status === EntityStatus.VIEW_ONLY ? (
          <div
            style={{
              paddingTop: sectionPadTop,
              display: 'flex',
              justifyContent: 'center',
            }}
          >
            {renderReactivationPanel()}
          </div>
        ) : (
          <>
            {restrictNonVatTabs && !pwaEntityTabsOnly && (
              <div
                style={{
                  paddingTop: sectionPadTop,
                  display: 'flex',
                  justifyContent: 'center',
                }}
              >
                <EntityNotConnectedPanel
                  maxWidth={1250}
                  onReconnect={connectOrganisation}
                />
              </div>
            )}

            {includeExcelReceiptsCapture &&
              organisation?.connectedProperly &&
              isExcelOrReceiptsProvider &&
              renderExcelReceiptsCapturePanels()}

            {showCaptureDocumentsPromo && (
              <div
                style={{
                  paddingTop: sectionPadTop,
                  display: 'flex',
                  justifyContent: 'center',
                }}
              >
                {renderCaptureDocumentsPromotionPanel()}
              </div>
            )}

            {/* Hide VAT checker panel when tax rates banner is shown or when Excel/Receipts provider */}
            {!restrictNonVatTabs &&
              !(
                selectedMaps?.length === 0 &&
                organisation?.connectedProperly &&
                organisation?.providerName?.toLowerCase() !==
                  Provider.EXCEL.toLowerCase() &&
                organisation?.providerName?.toLowerCase() !==
                  Provider.RECEIPTS.toLowerCase()
              ) &&
              organisation?.providerName?.toLowerCase() !==
                Provider.GROUP.toLowerCase() &&
              organisation?.providerName?.toLowerCase() !==
                Provider.EXCEL.toLowerCase() &&
              organisation?.providerName?.toLowerCase() !==
                Provider.RECEIPTS.toLowerCase() &&
              (!hasAdvancedUserRights ||
                hasAdvancedUserRight(
                  currentUser,
                  organisation as any,
                  AdvancedUserRightsCodes.VAT_NUMBER_CHECKER
                )) && (
                <div
                  style={{
                    paddingTop: sectionPadTop,
                    display: 'flex',
                    justifyContent: 'center',
                  }}
                >
                  {renderVatCheckerPanel()}
                </div>
              )}
          </>
        )}

        {/* Show tax rates promotion banner when entity is connected but has no mappings (hidden for Excel/Receipts/Group) */}
        {selectedMaps?.length === 0 &&
          organisation?.connectedProperly &&
          organisation?.status !== EntityStatus.VIEW_ONLY &&
          !isGroupProvider &&
          organisation?.providerName?.toLowerCase() !==
            Provider.EXCEL.toLowerCase() &&
          organisation?.providerName?.toLowerCase() !==
            Provider.RECEIPTS.toLowerCase() && (
            <div
              style={{
                paddingTop: sectionPadTop,
                display: 'flex',
                justifyContent: 'center',
              }}
            >
              <div
                style={{
                  maxWidth: 1250,
                  width: '100%',
                }}
              >
                <TaxRatesPromotionBanner
                  organisation={organisation}
                  fullWidth={true}
                  title={t('organisation.load-tax-rates')}
                />
              </div>
            </div>
          )}

        {isGroupProvider ? (
          renderGroupMappingsSummary()
        ) : pwaEntityTabsOnly ? (
          <PwaReturnsPage
            queue={pwaReturnsQueue}
            onQueueChange={setPwaReturnsQueue}
            items={pwaReturnListItems}
            onOpenReturn={(mappingId, dataLogId) => {
              if (!organisation?.connectionId) return;
              vatGenerate(organisation.connectionId, mappingId, dataLogId);
            }}
          />
        ) : (
          selectedMaps.map((m) => (
            <div
              id={m.mappingId}
              key={m.mappingId}
              style={{
                paddingTop: sectionPadTop,
                display: 'flex',
                justifyContent: 'center',
              }}
            >
              <MappingDetails
                mapping={m}
                countryName={
                  organisation?.returnTypesEdit.find(
                    (returnType) => returnType.id == m.returnTypeId
                  )?.countryName
                }
                vatReturnLogs={vatReturnLogs}
                connectionId={organisation?.connectionId}
                vatGenerate={vatGenerate}
                editReturnMapping={editReturnMapping}
                connected={!!organisation?.connectedProperly}
                currentUser={currentUser}
                handlePreferences={handlePreferences}
                organisation={organisation}
                hasAdvancedUserRights={hasAdvancedUserRights}
                updateReportFields={updateReportFields}
                allOrganisations={organisations}
                allMappings={mappings}
              />
            </div>
          ))
        )}

        {isGroupProvider ? (
          <div style={{ paddingTop: sectionPadTop }} />
        ) : (
          <>
            {pwaEntityTabsOnly ? null : selectedMaps?.length > 0 ? (
              <div
                style={{
                  paddingTop: sectionPadTop,
                  display: 'flex',
                  justifyContent: 'center',
                }}
              >
                <StyledCard
                  style={{
                    border: '1px dashed var(--color-primary, #41aaaf)',
                    boxShadow: '0px 4px 10px 0px #0000000D',
                    margin: inlineWithSidePanel ? 0 : '20px 0',
                    display: 'flex',
                    justifyContent: 'center',

                    maxWidth: 1250,
                    width: '100%' /*minWidth: 916.6*/,
                    overflow: 'auto',
                    fontSize: 14,
                    fontWeight: 400,
                  }}
                >
                  {organisationForInlineAddReturnButton && (
                    <AdddReturnButton
                      record={organisationForInlineAddReturnButton}
                      onClick={() => handleAddReturn()}
                      size="large"
                    />
                  )}
                </StyledCard>
              </div>
            ) : (
              <div
                style={{
                  paddingTop: sectionPadTop,
                  display: 'flex',
                  justifyContent: 'center',
                }}
              >
                <StyledCard
                  style={{
                    border: '1px dashed var(--color-primary, #41aaaf)',
                    boxShadow: '0px 4px 10px 0px #0000000D',
                    margin: inlineWithSidePanel ? 0 : '20px 0',
                    display: 'flex',
                    justifyContent: 'center',
                    padding: '94px',
                    maxWidth: 1250,
                    width: '100%' /*minWidth: 916.6*/,
                    overflow: 'auto',
                  }}
                >
                  <Space direction="vertical" align="center">
                    <CardTitleLargeSpan style={{ fontSize: 24 }}>
                      {pwaMobileNav
                        ? t('submissions.no-submissions-to-display')
                        : t('organisation.empty-tax-return')}
                    </CardTitleLargeSpan>
                    {!pwaMobileNav ? (
                      <>
                        <CardTitleSpan
                          style={{ fontSize: 16, fontWeight: 300 }}
                        >
                          {t('organisation.empty-tax-returns-action')}
                        </CardTitleSpan>
                        <StyledTooltip
                          placement="top"
                          title={
                            hasNoTransactions
                              ? t(
                                  'organisation.upload-transactions-before-add-return'
                                )
                              : hasAdvancedUserRights &&
                                !hasAdvancedUserRight(
                                  currentUser,
                                  organisation as any,
                                  AdvancedUserRightsCodes.CREATE_EDIT_RETURN
                                )
                              ? t('global.edit-return-rights-warning')
                              : ''
                          }
                        >
                          <ScopeButton
                            type="primary"
                            height="40px"
                            width="197px"
                            style={{ marginTop: '30px' }}
                            onClick={() => handleAddReturn()}
                            disabled={
                              hasNoTransactions ||
                              !isOrganisationReadyForAddReturn ||
                              (currentUser &&
                                AccountType[currentUser.accountType] ===
                                  AccountType.CLIENT) ||
                              (hasAdvancedUserRights &&
                                !hasAdvancedUserRight(
                                  currentUser,
                                  organisation as any,
                                  AdvancedUserRightsCodes.CREATE_EDIT_RETURN
                                ))
                            }
                          >
                            {t('organisation.add-tax-return')}
                          </ScopeButton>
                        </StyledTooltip>
                      </>
                    ) : null}
                  </Space>
                </StyledCard>
              </div>
            )}
          </>
        )}
      </div>
    );
  };

  /* const renderProviderLogo = () => {
    const provider = organisation?.providerName?.toLowerCase();

    switch (provider) {
      case 'quickbooks':
        return (
          <img
            src={ImgWithQBOSignIn}
            style={{ width: '36px', height: '36px' }}
          />
        );
      case 'zoho':
        return (
          <img
            src={ImgWithZohoSignIn}
            style={{ width: '36px', height: '36px' }}
          />
        );
      default:
        return (
          <img
            src={ImgWithXeroSignIn}
            style={{ width: '36px', height: '36px' }}
          />
        );
    }
  }; */

  const renderEntityDetailsCard = (opts?: { inlineBesideMain?: boolean }) => {
    return (
      <EntityDetailsCard
        organisation={organisation}
        currentUser={currentUser}
        menuSize={menuSize}
        windowWidth={windowWidth}
        windowHeight={windowHeight}
        onClose={() => setShowEntityDetails(false)}
        onEditFriendlyName={openEditFriendlyName}
        getCountry={getCountry}
        hasAttachments={
          Array.isArray(organisation?.organisationDetails?.attachments) &&
          organisation.organisationDetails.attachments.length > 0
        }
        downloadAttachment={downloadAttachment}
        ENTITY_DETAILS_BREAKPOINT={ENTITY_DETAILS_BREAKPOINT}
        ENTITY_DETAILS_HEIGHT_BREAKPOINT={ENTITY_DETAILS_HEIGHT_BREAKPOINT}
        inlineBesideMain={opts?.inlineBesideMain}
      />
    );
  };

  const renderVatTab = () => {
    // PWA: disconnected accounting entities → reconnect screen (not Tax Returns).
    if (pwaMobileNav && restrictNonVatTabs && organisation) {
      return (
        <div className="entity-vat-returns-tab scope-pwa-vat-returns-tab scope-pwa-entity-reconnect-tab">
          <PwaEntityDisconnected
            organisation={organisation}
            onReconnect={connectOrganisation}
          />
        </div>
      );
    }

    if (pwaEntityTabsOnly) {
      return (
        <div className="entity-vat-returns-tab scope-pwa-vat-returns-tab">
          {renderReturnsContent({ includeExcelReceiptsCapture: false })}
        </div>
      );
    }

    const vatReturnsWideResponsive =
      windowWidth > ENTITY_DETAILS_BREAKPOINT &&
      windowHeight > ENTITY_DETAILS_HEIGHT_BREAKPOINT;

    if (vatReturnsWideResponsive) {
      return (
        <div className="entity-vat-returns-tab--with-inline-side">
          <div className="capture-tab-wrapper">
            <div
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                width: '100%',
                gap: 20,
                alignItems: 'flex-start',
              }}
            >
              <div style={{ flex: 1, minWidth: 600 }}>
                {renderReturnsContent({
                  includeExcelReceiptsCapture: false,
                  inlineWithSidePanel: true,
                })}
              </div>
              <div
                style={{
                  width: TAX_RATES_PROMOTION_SIDE_PANEL_WIDTH,
                  flexShrink: 0,
                  overflow: 'visible',
                }}
              >
                {renderEntityDetailsCard({ inlineBesideMain: true })}
              </div>
            </div>
          </div>
        </div>
      );
    }

    return (
      <div
        className="entity-overview-tab"
        //style={{ minHeight: 'calc(100vh - 180px)' }}
      >
        <div
          style={{
            width:
              windowWidth > ENTITY_DETAILS_BREAKPOINT &&
              windowHeight > ENTITY_DETAILS_HEIGHT_BREAKPOINT
                ? menuSize === MenuSize.WIDE
                  ? 'calc(100vw - 552px)'
                  : 'calc(100vw - 415px)'
                : '100%',
            minWidth: 850,
            marginRight: '24px',
          }}
        >
          {renderReturnsContent({
            includeExcelReceiptsCapture: false,
          })}
        </div>

        {showEntityDetails ? (
          <div
            className="entity-narrow-side-panel"
            style={{
              height: 'calc(100vh - 180px)',
              position: 'fixed',
              float: 'right',
              right: 17,
            }}
          >
            {renderEntityDetailsCard()}
          </div>
        ) : (
          <button
            type="button"
            className="entity-narrow-side-panel-tab"
            onClick={() => setShowEntityDetails(true)}
          >
            <span className="entity-narrow-side-panel-tab__label">
              Entity Details
            </span>
          </button>
        )}
      </div>
    );
  };

  const renderCaptureTab = () => {
    const driveScannerEnabled = driveScannerEnabledForCapture;

    // Capture tab should always use the same 2-column layout (main + side panel),
    // regardless of provider. Non-receipts providers show the panel but disabled.
    // Use resolved organisationId (store-backed) rather than the prop, to avoid
    // first-render flicker where inline ConnectFolder mounts before the side panel.
    const showFolderSidePanel = Boolean(organisationId) && !isExcelProvider;
    // "Capture from a Folder" is available for all entities except Excel and Group.
    // Still respects the drive-scanner feature flag.
    const folderPanelDisabled =
      !driveScannerEnabled ||
      isExcelProvider ||
      isGroupProvider ||
      !scanPrerequisitesMet;

    const captureFolderWideResponsive =
      windowWidth > ENTITY_DETAILS_BREAKPOINT &&
      windowHeight > ENTITY_DETAILS_HEIGHT_BREAKPOINT;

    const capturePanels = renderExcelReceiptsCapturePanels({
      inlineWithSidePanel: captureFolderWideResponsive && showFolderSidePanel,
    });

    const captureSkeletonLayout: 'with-side-panel' | 'main-only' =
      showFolderSidePanel && captureFolderWideResponsive
        ? 'with-side-panel'
        : 'main-only';

    if (
      showCaptureSkeleton &&
      organisation?.status !== EntityStatus.VIEW_ONLY &&
      capturePanels
    ) {
      return (
        <div className="entity-capture-tab">
          <CaptureTabSkeleton layout={captureSkeletonLayout} />
        </div>
      );
    }

    const captureMainColumnStyle: React.CSSProperties = {
      width:
        showFolderSidePanel &&
        windowWidth > ENTITY_DETAILS_BREAKPOINT &&
        windowHeight > ENTITY_DETAILS_HEIGHT_BREAKPOINT
          ? menuSize === MenuSize.WIDE
            ? 'calc(100vw - 552px)'
            : 'calc(100vw - 415px)'
          : '100%',
      minWidth: 850,
      marginRight: '24px',
    };

    const pwaContentPad = pwaEntityTabsOnly ? '0' : '0 40px';

    const narrowCaptureInner = (
      <div
        style={{
          padding: pwaContentPad,
          width: '100%',
          margin: 'auto',
          maxWidth: pwaEntityTabsOnly ? undefined : 1250,
        }}
      >
        {organisation?.status === EntityStatus.VIEW_ONLY ? (
          <div
            style={{
              paddingTop: '20px',
              display: 'flex',
              justifyContent: 'center',
            }}
          >
            {renderReactivationPanel()}
          </div>
        ) : capturePanels ? (
          capturePanels
        ) : (
          <Alert
            type="info"
            showIcon
            style={{ marginTop: 24 }}
            message={t('organisation.capture-tab')}
            description={t('organisation.capture-not-available-description')}
          />
        )}
      </div>
    );

    if (
      pwaEntityTabsOnly ||
      organisation?.status === EntityStatus.VIEW_ONLY ||
      !showFolderSidePanel
    ) {
      return <div className="entity-capture-tab">{narrowCaptureInner}</div>;
    }

    if (!capturePanels) {
      return <div className="entity-capture-tab">{narrowCaptureInner}</div>;
    }

    const captureFolderConnectPanel = (
      <ConnectFolder
        connectionId={organisationId as string}
        layout="sidePanel"
        disabled={folderPanelDisabled}
        showDisabledMessage={isExcelProvider}
      />
    );

    if (captureFolderWideResponsive) {
      return (
        <div className="entity-capture-tab entity-capture-tab--with-inline-folder">
          <div className="capture-tab-wrapper">
            <div
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                width: '100%',
                gap: 20,
                alignItems: 'flex-start',
              }}
            >
              <div style={{ flex: 1, minWidth: 600 }}>{capturePanels}</div>
              <div
                style={{
                  width: TAX_RATES_PROMOTION_SIDE_PANEL_WIDTH,
                  flexShrink: 0,
                }}
              >
                {captureFolderConnectPanel}
              </div>
            </div>
          </div>
        </div>
      );
    }

    return (
      <div className="entity-overview-tab entity-capture-tab">
        <div style={captureMainColumnStyle}>
          <div
            style={{
              padding: '0 40px',
              width: '100%',
              maxWidth: 1250,
              margin: '0 auto',
            }}
          >
            {capturePanels}
          </div>
        </div>

        {showCaptureFolderPanel ? (
          <div
            className="entity-narrow-side-panel"
            style={{
              height: 'calc(100vh - 180px)',
              position: 'fixed',
              float: 'right',
              right: 17,
              width: TAX_RATES_PROMOTION_SIDE_PANEL_WIDTH,
              maxWidth: `min(${TAX_RATES_PROMOTION_SIDE_PANEL_WIDTH}px, 90vw)`,
              display: 'flex',
              flexDirection: 'column',
              overflow: 'hidden',
              boxSizing: 'border-box',
            }}
          >
            <div
              style={{
                flexShrink: 0,
                display: 'flex',
                justifyContent: 'flex-end',
                alignItems: 'center',
                padding: '6px 6px 2px',
                // background: 'var(--color-surface-raised, #fff)',
              }}
            >
              <button
                type="button"
                onClick={() => setShowCaptureFolderPanel(false)}
                aria-label={t('organisation.narrow-side-panel-close')}
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  width: 32,
                  height: 32,
                  padding: 0,
                  margin: 0,
                  border: 'none',
                  borderRadius: 6,
                  background: 'transparent',
                  cursor: 'pointer',
                  color: 'var(--color-text-muted, #5a6a6a)',
                }}
              >
                <CloseOutlined style={{ fontSize: 16 }} />
              </button>
            </div>
            <div
              style={{
                flex: 1,
                minHeight: 0,
                overflowY: 'auto',
                overflowX: 'hidden',
              }}
            >
              {captureFolderConnectPanel}
            </div>
          </div>
        ) : (
          <button
            type="button"
            className="entity-narrow-side-panel-tab"
            onClick={() => setShowCaptureFolderPanel(true)}
          >
            <span className="entity-narrow-side-panel-tab__label">
              {t('organisation.capture-side-folder-tab')}
            </span>
          </button>
        )}
      </div>
    );
  };

  const renderReferenceDataComingSoonPlaceholder = () => (
    <div className="entity-reference-placeholder-tab">
      <Alert
        type="info"
        showIcon
        message={t('group.coming-soon')}
        description={t('organisation.reference-data-soon-description')}
      />
    </div>
  );

  const renderTaxRatesTab = () => {
    return (
      <TaxRatesTab
        organisation={effectiveOrganisation}
        handlePreferences={handlePreferences}
      />
    );
  };
  const renderTrackingCategoriesTab = () => {
    return <TrackingCategoriesTab organisation={organisation} />;
  };
  const renderTransactionItemsTab = () => {
    return (
      <TransactionItemsTab
        organisation={effectiveOrganisation}
        organisationId={organisationId ?? ''}
      />
    );
  };
  const renderContactsTab = () => {
    return (
      <ContactsTab
        organisation={organisation}
        handlePreferences={handlePreferences}
      />
    );
  };

  const renderChartOfAccountsTab = () => {
    return (
      <ChartOfAccountsTab
        organisation={organisation}
        handlePreferences={handlePreferences}
      />
    );
  };

  const renderTransactionsTab = () => {
    return (
      <TransactionsTab
        organisationId={organisationId ?? ''}
        connectionId={effectiveOrganisation?.connectionId}
        organisation={effectiveOrganisation}
        handlePreferences={handlePreferences}
        captureDocumentsPromo={
          showCaptureDocumentsPromo
            ? renderCaptureDocumentsPromotionPanel()
            : undefined
        }
        onRegisterRefetchExcelTransactions={(fn) => {
          refetchExcelTransactionsFromTabRef.current = fn;
        }}
        onUploadFile={openUploadTransactions}
        onAddTransaction={undefined}
        onReceiptsUploadStart={
          effectiveOrganisation?.providerName?.toLowerCase() !==
          Provider.EXCEL.toLowerCase()
            ? (fileName?: string) => {
                if (fileName) {
                  setOptimisticReceiptRescanFileName(fileName);
                }
                dispatch(turnDrawerOff());
                resetPwaSheetDragState();
                setActiveTabKey('capture');
                syncTabToUrl('capture');
              }
            : undefined
        }
        rescanDrawerReopenSignal={rescanDrawerReopenSignal}
        onRegisterRescanDrawerReopen={(fn) => {
          rescanDrawerReopenHandlerRef.current = fn;
        }}
      />
    );
  };

  const renderIncomeStatementTab = () => (
    <IncomeStatementTab organisation={organisation} menuSize={menuSize} />
  );

  const renderReconciliationTab = () => {
    return (
      <ReconciliationTab
        organisation={organisation}
        isActive={activeTabKey === 'reconciliation'}
        menuSize={menuSize}
        selectedMaps={selectedMaps}
        loadCustomersAndSuppliers={loadCustomersAndSuppliers}
        activateVatChecker={activateVatChecker}
        openReconciliationDrawer={openReconciliationDrawer}
        onAddReturn={handleAddReturn}
        currentUser={currentUser}
        hasAdvancedUserRights={hasAdvancedUserRights}
        hasNoTransactions={hasNoTransactions}
        reconciliation={reconciliation}
        reconciliationRun={reconciliationRun}
        vatReturnLogs={vatReturnLogs}
        handlePreferences={handlePreferences}
        viewReconciliationData={viewReconciliationData}
      />
    );
  };

  const referenceMenuItems: MenuProps['items'] = [
    {
      key: 'tax-rates',
      disabled: lacksTaxRatesRight || restrictNonVatTabs,
      label:
        lacksTaxRatesRight || restrictNonVatTabs ? (
          <StyledTooltip
            placement="left"
            title={
              restrictNonVatTabs
                ? disconnectedEntityTabTooltip
                : t('global.edit-tax-rates-rights-warning')
            }
          >
            <span>{t('organisation.tax-rates')}</span>
          </StyledTooltip>
        ) : (
          t('organisation.tax-rates')
        ),
    },
    {
      key: 'chart-of-accounts',
      disabled: restrictNonVatTabs,
      label: restrictNonVatTabs ? (
        <StyledTooltip placement="left" title={disconnectedEntityTabTooltip}>
          <span>{t('organisation.chart-of-accounts')}</span>
        </StyledTooltip>
      ) : (
        t('organisation.chart-of-accounts')
      ),
    },
    ...(shouldFetchTrackingCategories
      ? [
          {
            key: 'transaction-items',
            disabled: restrictNonVatTabs,
            label: restrictNonVatTabs ? (
              <StyledTooltip
                placement="left"
                title={disconnectedEntityTabTooltip}
              >
                <span>{t('organisation.transaction-items')}</span>
              </StyledTooltip>
            ) : (
              t('organisation.transaction-items')
            ),
          },
          {
            key: 'tracking-categories',
            disabled: restrictNonVatTabs,
            label: restrictNonVatTabs ? (
              <StyledTooltip
                placement="left"
                title={disconnectedEntityTabTooltip}
              >
                <span>{t('organisation.reference-tracking-categories')}</span>
              </StyledTooltip>
            ) : (
              t('organisation.reference-tracking-categories')
            ),
          },
        ]
      : []),
    {
      key: 'contacts',
      disabled: restrictNonVatTabs,
      label: restrictNonVatTabs ? (
        <StyledTooltip placement="left" title={disconnectedEntityTabTooltip}>
          <span>{t('organisation.reference-contacts')}</span>
        </StyledTooltip>
      ) : (
        t('organisation.reference-contacts')
      ),
    },
  ];

  const onReferenceMenuClick: MenuProps['onClick'] = ({ key }) => {
    handleTabChange(String(key));
  };

  const isReferenceDropdownActive = (
    ENTITY_REFERENCE_TAB_KEYS as readonly string[]
  ).includes(activeTabKey);

  const renderActiveEntityPanel = () => {
    if (
      restrictNonVatTabs &&
      (ENTITY_TABS_RESTRICTED_WHEN_DISCONNECTED as readonly string[]).includes(
        activeTabKey
      )
    ) {
      return renderVatTab();
    }

    switch (activeTabKey) {
      case 'vat':
        return renderVatTab();
      case 'capture':
        return showCaptureAndTransactionsTabs
          ? renderCaptureTab()
          : renderVatTab();
      case 'transactions':
        return showCaptureAndTransactionsTabs
          ? renderTransactionsTab()
          : renderVatTab();
      case 'reconciliation':
        return renderReconciliationTab();
      case 'income-statement':
        return isExcelOrReceiptsProvider
          ? renderIncomeStatementTab()
          : renderVatTab();
      case 'tax-rates':
        return renderTaxRatesTab();
      case 'chart-of-accounts':
        return renderChartOfAccountsTab();
      case 'transaction-items':
        return shouldFetchTrackingCategories
          ? renderTransactionItemsTab()
          : renderVatTab();
      case 'tracking-categories':
        return shouldFetchTrackingCategories
          ? renderTrackingCategoriesTab()
          : renderVatTab();
      case 'contacts':
        return renderContactsTab();
      default:
        return renderVatTab();
    }
  };

  const renderActiveEntityPanelSafely = () => {
    try {
      return renderActiveEntityPanel();
    } catch (error) {
      // Temporary guard to prevent refresh-time hard crash while we isolate the source-map-misaligned runtime error.
      // eslint-disable-next-line no-console
      console.error('OrganisationDetails renderActiveEntityPanel failed', {
        error,
        organisationId,
        activeTabKey,
      });
      return null;
    }
  };

  const reconciliationTabLabelInner = (
    <span style={{ position: 'relative', display: 'inline-block' }}>
      {showReconciliationMarker && (
        <span
          style={{
            position: 'absolute',
            top: 0,
            right: -8,
            width: 6,
            height: 6,
            borderRadius: '50%',
            backgroundColor: FEATURE_DOT_COLOR,
            pointerEvents: 'none',
          }}
          aria-hidden
        />
      )}
      {t('organisation.reconciliation-tab')}
    </span>
  );

  /* const onDisconnectOrganisation = (organisationId: string | undefined) => {
    organisationId && disconnectOrganisation(organisationId);
  }; */

  const entityToSend = {
    organisation: organisation?.friendlyName,
    users: organisation?.users,
    organisationId: organisation?.uuid,
  } as Entity;

  const updateReportFields = (
    dataLogId: string,
    reference: string,
    returnTax: string
  ) => {
    console.debug('[updateReportFields][index] dispatching action', {
      dataLogId,
      reference,
      returnTax,
    });
    dispatch(updateReportFieldsRequest(dataLogId, reference, returnTax));
  };

  const renderEntityNavTabButton = (
    tabKey: (typeof ENTITY_MAIN_TAB_KEYS)[number]
  ) => {
    const active = activeTabKey === tabKey;
    let labelInner: React.ReactNode;
    if (tabKey === 'vat') {
      labelInner = t('main.tax-returns');
    } else if (tabKey === 'capture') {
      labelInner = (
        <span style={{ position: 'relative', display: 'inline-block' }}>
          {showCaptureMarker && !restrictNonVatTabs && (
            <span
              style={{
                position: 'absolute',
                top: 0,
                right: -8,
                width: 6,
                height: 6,
                borderRadius: '50%',
                backgroundColor: FEATURE_DOT_COLOR,
                pointerEvents: 'none',
              }}
              aria-hidden
            />
          )}
          {pwaEntityTabsOnly
            ? t('organisation.capture-dashboard-section')
            : t('organisation.capture-tab')}
        </span>
      );
    } else if (tabKey === 'transactions') {
      labelInner = (
        <span style={{ position: 'relative', display: 'inline-block' }}>
          {showTransactionsMarker && (
            <span
              style={{
                position: 'absolute',
                top: 0,
                right: -8,
                width: 6,
                height: 6,
                borderRadius: '50%',
                backgroundColor: FEATURE_DOT_COLOR,
                pointerEvents: 'none',
              }}
              aria-hidden
            />
          )}
          <span>
            {t('organisation.transactions')}
            {showCaptureAndTransactionsTabs &&
            !isExcelProvider &&
            transactionsToReviewCount > 0
              ? ` (${formatDisplayInteger(
                  transactionsToReviewCount,
                  i18n.language
                )})`
              : ''}
          </span>
        </span>
      );
    } else if (tabKey === 'income-statement') {
      labelInner = t('organisation.income-statement-tab');
    } else {
      labelInner = reconciliationTabDisabled ? (
        <StyledTooltip placement="top" title={t('group.coming-soon')}>
          {reconciliationTabLabelInner}
        </StyledTooltip>
      ) : (
        reconciliationTabLabelInner
      );
    }

    const disabledTransactions =
      tabKey === 'transactions' &&
      (isReceiptsBatchInProgress || restrictNonVatTabs);
    const disabledReconciliation =
      tabKey === 'reconciliation' &&
      (reconciliationTabDisabled || restrictNonVatTabs);
    const disabledCapture = tabKey === 'capture' && restrictNonVatTabs;
    const disabledIncomeStatement =
      tabKey === 'income-statement' && incomeStatementTabDisabled;
    const isTabDisabled =
      disabledTransactions ||
      disabledReconciliation ||
      disabledCapture ||
      disabledIncomeStatement;

    const transactionsTooltip = isReceiptsBatchInProgress
      ? t('organisation.receipts-upload-available')
      : restrictNonVatTabs && tabKey === 'transactions'
      ? disconnectedEntityTabTooltip
      : '';

    const disconnectedTabTooltip =
      restrictNonVatTabs &&
      (tabKey === 'capture' || tabKey === 'reconciliation')
        ? disconnectedEntityTabTooltip
        : '';

    const incomeStatementTooltip =
      tabKey === 'income-statement' && incomeStatementTabDisabled
        ? t('organisation.income-statement-tab-coming-soon')
        : '';

    const button = (
      <button
        key={tabKey}
        type="button"
        className={`entity-details-tab-nav-btn${
          active ? ' entity-details-tab-nav-btn-active' : ''
        }${isTabDisabled ? ' entity-details-tab-nav-btn-disabled' : ''}`}
        onClick={() => handleTabChange(tabKey)}
        disabled={isTabDisabled}
        aria-disabled={isTabDisabled}
      >
        {labelInner}
      </button>
    );

    if (tabKey === 'transactions' && transactionsTooltip) {
      return (
        <StyledTooltip key={tabKey} placement="top" title={transactionsTooltip}>
          <span style={{ display: 'inline-flex' }}>{button}</span>
        </StyledTooltip>
      );
    }

    if (disconnectedTabTooltip) {
      return (
        <StyledTooltip
          key={tabKey}
          placement="top"
          title={disconnectedTabTooltip}
        >
          <span style={{ display: 'inline-flex' }}>{button}</span>
        </StyledTooltip>
      );
    }

    if (incomeStatementTooltip) {
      return (
        <StyledTooltip
          key={tabKey}
          placement="top"
          title={incomeStatementTooltip}
        >
          <span style={{ display: 'inline-flex' }}>{button}</span>
        </StyledTooltip>
      );
    }

    return button;
  };

  return (
    <EntityDetailsWrapper
      className={`entity-details-page${
        pwaEntityTabsOnly || (pwaMobileNav && restrictNonVatTabs)
          ? ' scope-pwa-entity-details'
          : ''
      }`}
    >
      <ShowUploadsPanelContext.Provider
        value={{ showUploadsPanel, setShowUploadsPanel }}
      >
        <Spin
          spinning={loading}
          size="large"
          wrapperClassName="entity-details-spin-wrap"
        >
          {pwaMobileNav && organisation ? (
            <PwaEntityBar
              organisation={organisation}
              actions={
                activeTabKey === 'transactions' &&
                pwaTransactionsActions?.onActivity ? (
                  <button
                    type="button"
                    className="pwa-iconbtn subtle"
                    aria-label={t('organisation.reconciliation-activity', {
                      defaultValue: 'Activity',
                    })}
                    onClick={pwaTransactionsActions.onActivity}
                  >
                    <PwaIcon name="bell" />
                  </button>
                ) : undefined
              }
            />
          ) : null}
          {pwaEntityTabsOnly ? (
            /* Bottom nav (Capture / Review / Returns) replaces the in-page tab strip. */
            null
          ) : (
            <nav
              className="entity-details-tab-nav-wrap"
              aria-label={t('common.entity')}
              ref={tabNavRef}
              onWheel={handleTabNavWheel}
            >
              <>
                {ENTITY_LEADING_TAB_KEYS.filter((tabKey) =>
                  tabKey === 'vat' ? true : showCaptureAndTransactionsTabs
                ).map((tabKey) => renderEntityNavTabButton(tabKey))}
                {isExcelOrReceiptsProvider
                  ? renderEntityNavTabButton('income-statement')
                  : renderEntityNavTabButton('reconciliation')}
                <Dropdown
                  trigger={
                    restrictNonVatTabs || isGroupProvider ? [] : ['click']
                  }
                  menu={{
                    items: referenceMenuItems,
                    onClick: onReferenceMenuClick,
                  }}
                >
                  {restrictNonVatTabs || isGroupProvider ? (
                    <StyledTooltip
                      placement="top"
                      title={
                        isGroupProvider
                          ? t(
                              'organisation.reference-data-unavailable-for-group'
                            )
                          : disconnectedEntityTabTooltip
                      }
                    >
                      <span style={{ display: 'inline-flex' }}>
                        <button
                          type="button"
                          className={`entity-details-tab-nav-btn entity-details-tab-nav-btn-reference${
                            isReferenceDropdownActive
                              ? ' entity-details-tab-nav-btn-active'
                              : ''
                          }`}
                          disabled
                        >
                          {t('organisation.reference-data-menu')}
                          <DownOutlined className="entity-details-drop-icon" />
                        </button>
                      </span>
                    </StyledTooltip>
                  ) : (
                    <button
                      type="button"
                      className={`entity-details-tab-nav-btn entity-details-tab-nav-btn-reference${
                        isReferenceDropdownActive
                          ? ' entity-details-tab-nav-btn-active'
                          : ''
                      }`}
                    >
                      {t('organisation.reference-data-menu')}
                      <DownOutlined className="entity-details-drop-icon" />
                    </button>
                  )}
                </Dropdown>
              </>
            </nav>
          )}
          <div className="entity-details-tab-panel">
            {renderActiveEntityPanelSafely()}
          </div>

          {!pwaMobileNav &&
            ((parentGroupOrganisation && !isGroupProvider) ||
              (currentUser &&
                AccountType[currentUser.accountType] !==
                  AccountType.CLIENT)) && (
              <div className="top-wrapper">
                {parentGroupOrganisation && !isGroupProvider && (
                  <StyledTooltip title={t('group.entity-page-go-to-group')}>
                    <ColoredTag
                      color="var(--color-status-success, #058181)" //@ts-ignore
                      bgcolor="var(--color-status-success-bg, #E9F6F7)"
                      bordered={true}
                      bordercolor="var(--color-border, #dbeae3)"
                      icon={<TeamOutlined />}
                      style={{ maxWidth: 280, margin: '7px 0' }}
                      onClick={() =>
                        navigate(
                          `${routes.organisations.organisation.replace(
                            ':id',
                            parentGroupOrganisation.uuid
                          )}`
                        )
                      }
                    >
                      {t('group.entity-page-in-group', {
                        name: parentGroupOrganisation.friendlyName,
                      })}
                    </ColoredTag>
                  </StyledTooltip>
                )}

                {currentUser &&
                  AccountType[currentUser.accountType] !==
                    AccountType.CLIENT && (
                    <>
                      {isOrganisationConnectedForStatus ? (
                        <ColoredTag
                          color="var(--color-status-success, #058181)" //@ts-ignore
                          bgcolor="var(--color-status-success-bg, #E9F6F7)"
                          bordered={true}
                          bordercolor="var(--color-border, #dbeae3)"
                          icon={<CheckOutlined />}
                          style={{ margin: '7px 0' }}
                        >
                          {t('common.active')}
                        </ColoredTag>
                      ) : organisation?.providerName === Provider.EXCEL ||
                        organisation?.providerName === Provider.RECEIPTS ? (
                        <ColoredTag
                          color="var(--color-status-success, #058181)" //@ts-ignore
                          bgcolor="var(--color-status-success-bg, #E9F6F7)"
                          bordered={true}
                          bordercolor="var(--color-border, #dbeae3)"
                          icon={<CheckOutlined />}
                          style={{ margin: '7px 0' }}
                        >
                          {organisation?.status
                            ?.toLowerCase()
                            .replace(/_/g, ' ')
                            .replace(/\b\w/g, (c) => c.toUpperCase())}
                        </ColoredTag>
                      ) : organisation?.status === EntityStatus.VIEW_ONLY ? (
                        <StyledTooltip
                          title={t('organisation.view-only-state-tooltip')}
                        >
                          <ColoredTag
                            color="var(--color-view-only-text, #5a6a6a)" //@ts-ignore
                            bgcolor="var(--color-view-only-bg, #eaeaea)"
                            bordered={true}
                            bordercolor="var(--color-view-only-border, #d7d7d7)"
                            icon={<LockOutlined />}
                            style={{ margin: '7px 0' }}
                          >
                            {organisation?.status
                              ?.toLowerCase()
                              .replace(/_/g, ' ')
                              .replace(/\b\w/g, (c) => c.toUpperCase())}
                          </ColoredTag>
                        </StyledTooltip>
                      ) : (
                        <IconButton
                          type="default"
                          style={{
                            height: '30px',
                            borderRadius: 10,
                            display: 'flex',
                            alignItems: 'center',
                            padding: '14px 10px',
                            margin: '7px 0',
                          }}
                          icon={<RefreshIcon width={'18px'} height={'18px'} />}
                          onClick={() => connectOrganisation()}
                          disabled={
                            currentUser &&
                            AccountType[currentUser.accountType] ===
                              AccountType.CLIENT
                          }
                        >
                          {t('common.reconnect')}
                        </IconButton>
                      )}

                      <AvatarGroupRenderer
                        entity={entityToSend}
                        maxCount={4}
                        size={AvatarSizes.DEFAULT}
                        expandToggleLeft
                      />
                    </>
                  )}
              </div>
            )}
        </Spin>
      </ShowUploadsPanelContext.Provider>
      {!isExcelProvider && organisationId && (
        <SseNotifications
          id={organisationId}
          setMessage={setSseMessage}
          type="entity"
        />
      )}
    </EntityDetailsWrapper>
  );
};
